import * as turf from '@turf/turf';
import type { Map as MapboxMap } from 'mapbox-gl';
import { getCssVariable } from '@/libs/utils/getCssVariable';
import type { ILocation, IMapboxDirectionsResponse } from '@/types';
import type { ICapturedAadtFeatureCollection, IDealSummary, IGeoJSONGeometry } from '@/types/api';
import type { CapturedAadtSegmentId, StationChargeFilters, TrafficFilters } from '../constants';
import {
  CAPTURED_AADT_SEGMENT_COLORS,
  CLUSTER_COUNT_PROPERTY,
  CLUSTER_MAX_ZOOM,
  CLUSTER_MIN_POINTS,
  CLUSTER_RADIUS,
  COMPETITOR_HEATMAP_GRID_CELLS,
  COMPETITOR_HEATMAP_RAMP,
  COMPETITORS_DENSE_CHARGES_FLOOR,
  COMPETITORS_DENSE_MIN_ZOOM,
  COMPETITORS_FADE_DURATION_MS,
  COMPETITORS_FADE_LEAD_ZOOM,
  COMPETITORS_HEATMAP_MIN_ZOOM,
  COMPETITORS_SPARSE_MAX_NEIGHBORS,
  COMPETITORS_TILES_MAX_ZOOM,
  COMPETITORS_TILES_MIN_ZOOM,
  COMPETITORS_TILES_SOURCE_LAYER,
  CPO_ELECTRA,
  LayerId,
  MarkKind,
  SourceId,
  STATION_COLORS,
  TRAFFIC_COLORS,
  TRAFFIC_TILES_MAX_ZOOM,
  TRAFFIC_TILES_MIN_ZOOM,
  TRAFFIC_TILES_SOURCE_LAYER,
  UNKNOWN_STAGE_STYLE,
  ZONE_CLUSTER_OWNER_COUNT_PREFIX,
  ZONE_CLUSTER_TYPE_COUNT_PREFIX,
  ZONE_CLUSTERS_MIN_ZOOM,
  ZONES_MIN_ZOOM,
  ZONES_TILES_MAX_ZOOM,
  ZONES_TILES_SOURCE_LAYER,
} from '../constants';
import { electraMarkerId } from '../constants/stationIcons';
import {
  CHIP_COUNT_COLOR,
  CHIP_SIZE_STEPS,
  CLUSTER_GLYPH_ID,
  clusterChipIconId,
} from './clusterChipMark';
import { toClusteredMarkFeatures } from './clusteredMarkFeatures';
import { toDealFeatureCollection } from './dealFeatures';
import { DEAL_RING_ICON_SIZE, dealDotIconId, dealRingIconId } from './dealMarkerIcon';
import { STATION_CHARGE_RANGE_BOUNDS, stationChargeIconStep } from './getStationByCharges';
import { getTrafficByAADT, TRAFFIC_THRESHOLDS } from './getTrafficByAADT';
import {
  HIGHEST_COMPETITOR_CROWN_ICON,
  HIGHEST_COMPETITOR_CROWN_OFFSET_Y,
} from './highestCompetitorCrown';
import { chargingStationModelLayer, type StationModelVariant } from './stationModel';
import {
  ZONE_TYPE_COLORS,
  ZONE_TYPES,
  type ZoneType,
  zoneTypeExpression,
  zoneTypeFromName,
} from './zoneColors';
import { SELECTED_ZONE_HEX_ICON } from './zoneHexagon';

const MAX_AADT_CIRCLE_RADIUS_KM = 0.04;

const COMPETITOR_CHARGES_FIELD: mapboxgl.ExpressionSpecification = [
  'coalesce',
  ['get', 'charges_per_station_per_day'],
  0,
];

// Old tiles without density retain the conservative, dense-neighborhood tier.
const COMPETITOR_NEIGHBORS_FIELD: mapboxgl.ExpressionSpecification = [
  'coalesce',
  ['get', 'nearby_hpc_count'],
  COMPETITORS_SPARSE_MAX_NEIGHBORS + 1,
];

/**
 * One entry per tier of the backend's level-of-detail ladder, partitioning the
 * source. Two layers rather than one expression because Mapbox does not
 * transition a data-driven `icon-opacity`.
 */
const COMPETITOR_TIERS = [
  {
    layerId: LayerId.COMPETITORS_TILES,
    minZoom: COMPETITORS_TILES_MIN_ZOOM,
    fadeZoom: COMPETITORS_TILES_MIN_ZOOM + COMPETITORS_FADE_LEAD_ZOOM,
    filter: [
      'any',
      ['==', ['get', 'cpo'], CPO_ELECTRA],
      ['>=', COMPETITOR_CHARGES_FIELD, COMPETITORS_DENSE_CHARGES_FLOOR],
      ['<=', COMPETITOR_NEIGHBORS_FIELD, COMPETITORS_SPARSE_MAX_NEIGHBORS],
    ] as mapboxgl.FilterSpecification,
  },
  {
    layerId: LayerId.COMPETITORS_TILES_DENSE,
    minZoom: COMPETITORS_DENSE_MIN_ZOOM,
    fadeZoom: COMPETITORS_DENSE_MIN_ZOOM + COMPETITORS_FADE_LEAD_ZOOM,
    filter: [
      'all',
      ['!=', ['get', 'cpo'], CPO_ELECTRA],
      ['<', COMPETITOR_CHARGES_FIELD, COMPETITORS_DENSE_CHARGES_FLOOR],
      ['>', COMPETITOR_NEIGHBORS_FIELD, COMPETITORS_SPARSE_MAX_NEIGHBORS],
    ] as mapboxgl.FilterSpecification,
  },
] as const;

/** Densest first, for callers that treat the tiers as one overlay. */
export const COMPETITOR_TIER_LAYER_IDS = COMPETITOR_TIERS.map((tier) => tier.layerId);

/** The painted field and the invisible circles that make each cell hoverable. */
export const COMPETITOR_HEATMAP_LAYER_IDS = [
  LayerId.COMPETITORS_HEATMAP,
  LayerId.COMPETITORS_HEATMAP_HIT,
] as const;

/**
 * Sessions per day onto the 0–1 weight Mapbox clamps. The station property is
 * the half-zoom where tiles have switched to markers and the icons are still fading in.
 */
const COMPETITOR_HEATMAP_SESSIONS: mapboxgl.ExpressionSpecification = [
  'coalesce',
  ['get', 'last_quartile_sessions_per_day'],
  ['get', 'charges_per_station_per_day'],
  0,
];

const COMPETITOR_HEATMAP_WEIGHT: mapboxgl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  COMPETITOR_HEATMAP_SESSIONS,
  0,
  0,
  ...COMPETITOR_HEATMAP_RAMP.flatMap((stop) => [stop.sessions, stop.density]),
];

const COMPETITOR_HEATMAP_INTENSITY: mapboxgl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  COMPETITORS_HEATMAP_MIN_ZOOM,
  0.6,
  COMPETITORS_TILES_MIN_ZOOM,
  1.4,
];

// One vector tile is 512px. At a whole zoom the cell centers sit one cell apart.
// The brush is a Gaussian with sigma = radius/3, so 1.5 cell spacings keeps the
// midpoint between cells near half the peak and the cells read as one field.
const HEATMAP_TILE_PIXELS = 512;
const HEATMAP_CELL_PIXELS = HEATMAP_TILE_PIXELS / COMPETITOR_HEATMAP_GRID_CELLS;
const HEATMAP_RADIUS_ON_GRID = HEATMAP_CELL_PIXELS * 1.5;
const HEATMAP_RADIUS_STRETCHED = HEATMAP_RADIUS_ON_GRID * 2;

// The same tile stretches until the next whole zoom, so the centers drift from
// one cell apart to two. The brush doubles with them (exponential base 2 tracks
// that stretch), then snaps back when the next grid loads. Past the last grid
// zoom the z9 tile keeps stretching until the stations fade in.
// `scale` is folded into the stops: Mapbox only accepts `zoom` at the top of
// a paint expression, so the hit radius cannot wrap this one in a `*`.
function competitorHeatmapRadius(scale: number): mapboxgl.ExpressionSpecification {
  const onGrid = HEATMAP_RADIUS_ON_GRID * scale;
  const stretched = HEATMAP_RADIUS_STRETCHED * scale;
  const stops: number[] = [];
  const lastGridZoom = COMPETITORS_TILES_MIN_ZOOM - 1;
  for (let zoom = COMPETITORS_HEATMAP_MIN_ZOOM; zoom < lastGridZoom; zoom += 1) {
    stops.push(zoom, onGrid, zoom + 0.999, stretched);
  }
  stops.push(lastGridZoom, onGrid, COMPETITORS_TILES_MIN_ZOOM, stretched);
  return ['interpolate', ['exponential', 2], ['zoom'], ...stops];
}

const COMPETITOR_HEATMAP_RADIUS = competitorHeatmapRadius(1);

// The kernel falls to about 1% of its peak at the full brush (3 sigma). Half of
// that (1.5 sigma) is where the colour is still clearly painted, so the card
// opens on the blob the eye sees and stays quiet on the faint tail.
const COMPETITOR_HEATMAP_HIT_RADIUS = competitorHeatmapRadius(0.5);

/** Station colors. A low quartile stays pale purple; a high quartile reaches yellow. */
const COMPETITOR_HEATMAP_COLOR: mapboxgl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['heatmap-density'],
  0,
  'rgba(0, 0, 0, 0)',
  ...COMPETITOR_HEATMAP_RAMP.flatMap((stop) => [stop.density, stop.color]),
];

/** Holds until the station icons finish fading in, then leaves. */
const COMPETITOR_HEATMAP_OPACITY: mapboxgl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  COMPETITORS_TILES_MIN_ZOOM,
  0.75,
  COMPETITORS_TILES_MIN_ZOOM + COMPETITORS_FADE_LEAD_ZOOM,
  0,
];

/** Where the icons are fully in and the field must already be gone. */
const COMPETITOR_HEATMAP_MAX_ZOOM = COMPETITORS_TILES_MIN_ZOOM + COMPETITORS_FADE_LEAD_ZOOM;

/** Style-spec ceiling. The mark stays up through the 3D station models. */
const HIGHEST_COMPETITOR_MARK_MAX_ZOOM = 24;

function highestCompetitorMinZoom(performance: number, nearbyHpcCount?: unknown) {
  const sparse =
    typeof nearbyHpcCount === 'number' && nearbyHpcCount <= COMPETITORS_SPARSE_MAX_NEIGHBORS;
  return performance >= COMPETITORS_DENSE_CHARGES_FLOOR || sparse
    ? COMPETITORS_TILES_MIN_ZOOM
    : COMPETITORS_DENSE_MIN_ZOOM;
}

const highestCompetitorFadeZoom = new WeakMap<MapboxMap, number>();
const highestCompetitorFadeHandlers = new WeakMap<MapboxMap, () => void>();
const highestCompetitorSnapHandlers = new WeakMap<
  MapboxMap,
  (event: mapboxgl.MapSourceDataEvent) => void
>();
/** Stored winner. The drawn point may be the tile's snapped copy of it. */
const highestCompetitorTarget = new WeakMap<
  MapboxMap,
  { longitude: number; latitude: number; performance: number }
>();
const highestCompetitorPlaced = new WeakMap<MapboxMap, { longitude: number; latitude: number }>();

/**
 * Station tiles start at zoom 10 on a 4096-unit grid. Half a cell there is
 * about 5 m at the equator, and that is as far as the drawn pin can sit from
 * the stored point. A wider search crowns the station next door.
 */
const TILE_STATION_SNAP_METERS = 6;

/** The tile repeats the stored charges. This covers the encoding round trip. */
const TILE_STATION_CHARGE_MATCH = 0.01;

function distanceMeters(fromLon: number, fromLat: number, toLon: number, toLat: number) {
  const lat = ((fromLat + toLat) / 2) * (Math.PI / 180);
  const east = (toLon - fromLon) * 111_320 * Math.cos(lat);
  const north = (toLat - fromLat) * 110_540;
  return Math.hypot(east, north);
}

function bindHighestCompetitorFade(map: MapboxMap) {
  if (highestCompetitorFadeHandlers.has(map)) return;

  const handler = () => {
    new MapLayersService(map).syncHighestPerformingCompetitorFade();
  };
  highestCompetitorFadeHandlers.set(map, handler);
  map.on('zoom', handler);
}

function unbindHighestCompetitorFade(map: MapboxMap) {
  const handler = highestCompetitorFadeHandlers.get(map);
  if (!handler) return;

  map.off('zoom', handler);
  highestCompetitorFadeHandlers.delete(map);
}

function bindHighestCompetitorSnap(map: MapboxMap) {
  if (highestCompetitorSnapHandlers.has(map)) return;

  const handler = (event: mapboxgl.MapSourceDataEvent) => {
    if (event.sourceId !== SourceId.COMPETITORS_TILES || !event.isSourceLoaded) return;
    // A metadata event carries no station geometry.
    if (event.sourceDataType === 'metadata') return;
    new MapLayersService(map).snapHighestPerformingCompetitorToTile();
  };
  highestCompetitorSnapHandlers.set(map, handler);
  map.on('sourcedata', handler);
}

function unbindHighestCompetitorSnap(map: MapboxMap) {
  const handler = highestCompetitorSnapHandlers.get(map);
  if (!handler) return;

  map.off('sourcedata', handler);
  highestCompetitorSnapHandlers.delete(map);
}

function competitorTierFilter(layerId: string) {
  return COMPETITOR_TIERS.find((tier) => tier.layerId === layerId)?.filter;
}

/** Icon per charge range, with our own stations carrying the Electra marker. */
const STATION_SYMBOL_LAYOUT: mapboxgl.SymbolLayerSpecification['layout'] = {
  'icon-image': [
    'case',
    ['==', ['get', 'cpo'], CPO_ELECTRA],
    stationChargeIconStep(electraMarkerId) as mapboxgl.ExpressionSpecification,
    stationChargeIconStep(
      (rangeKey) => STATION_COLORS[rangeKey].id,
    ) as mapboxgl.ExpressionSpecification,
  ],
  'icon-size': 1,
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
};

/**
 * Hunting-zones overlay style: type-colored
 * translucent fill + a solid outline read as territory without drowning the
 * dense analytical layers. Type is carried by color; identity by click —
 * zones have no hover state; the cursor still swaps to a pointer on hover
 * (see useZoneInteractions) to signal they're clickable.
 *
 * This is the NEIGHBOUR (de-emphasised) baseline value. It intentionally
 * sits at the bottom of the two-tier emphasis hierarchy — see the table below.
 */
const ZONES_FILL_OPACITY = 0.03;
const ZONES_OUTLINE_WIDTH = 1.5;
const ZONES_POINT_RADIUS = 6;

const ZONES_OUTLINE_OPACITY = 1;
const ZONES_POINT_OPACITY = ZONES_FILL_OPACITY * 2;

// `0` leads the sum so a single term still satisfies `+`, which takes two operands.
function zoneClusterCount(
  selection: OwnerSelection,
  types: readonly ZoneType[] = ZONE_TYPES,
): mapboxgl.ExpressionSpecification {
  if (types.length === 0) return ['literal', 0];
  if (!ZONE_TYPES.every((type) => types.includes(type))) {
    return ['+', 0, ...types.map((type) => zoneClusterTypeCount(selection, type))];
  }
  if (selection.length === 0) return ['get', 'point_count'];

  const terms: mapboxgl.ExpressionSpecification[] = selection.map((email) => [
    'coalesce',
    ['get', `${ZONE_CLUSTER_OWNER_COUNT_PREFIX}${email}`],
    0,
  ]);
  return ['+', 0, ...terms];
}

function zoneClusterTypeCount(
  selection: OwnerSelection,
  type: ZoneType,
): mapboxgl.ExpressionSpecification {
  if (selection.length === 0) {
    return ['coalesce', ['get', `${ZONE_CLUSTER_TYPE_COUNT_PREFIX}${type}`], 0];
  }
  return [
    '+',
    0,
    ...selection.map((email) => [
      'coalesce',
      ['get', `${ZONE_CLUSTER_OWNER_COUNT_PREFIX}${email}|${type}`],
      0,
    ]),
  ];
}

/** The radius is the glanceable read of the count; the label is the precise one. */
function zoneClusterRadius(count: mapboxgl.ExpressionSpecification) {
  return ['step', count, 12, 10, 16, 50, 22] as mapboxgl.ExpressionSpecification;
}
/** Context under the cluster chip, so it is the bubble that recedes and not the counts. */
const ZONE_CLUSTER_OPACITY = 0.45;
const ZONE_CLUSTER_LABEL_COLOR = '#FFFFFF';
const ZONE_CLUSTER_LABEL_SIZE = 12;
const ZONE_CLUSTER_INNER_RING_WIDTH = 3;
const ZONE_CLUSTER_OUTER_RING_WIDTH = 4;

/** Rings consume space inside the existing count-scaled radius, never enlarge the bubble. */
function zoneClusterPaint(selection: OwnerSelection, types: readonly ZoneType[] = ZONE_TYPES) {
  const count = zoneClusterCount(selection, types);
  const counts: Record<ZoneType, mapboxgl.ExpressionSpecification> = {
    H: ['var', 'H'],
    R: ['var', 'R'],
    UH: ['var', 'UH'],
  };
  const bind = (expression: mapboxgl.ExpressionSpecification): mapboxgl.ExpressionSpecification =>
    [
      'let',
      ...ZONE_TYPES.flatMap((type) => [
        type,
        types.includes(type) ? zoneClusterTypeCount(selection, type) : ['literal', 0],
      ]),
      expression,
    ] as mapboxgl.ExpressionSpecification;
  const present: mapboxgl.ExpressionSpecification = [
    '+',
    0,
    ...ZONE_TYPES.map((type) => ['case', ['>', counts[type], 0], 1, 0]),
  ];
  const innerWidth: mapboxgl.ExpressionSpecification = [
    'case',
    ['>', present, 1],
    ZONE_CLUSTER_INNER_RING_WIDTH,
    0,
  ];
  const outerWidth: mapboxgl.ExpressionSpecification = [
    'case',
    ['>', present, 2],
    ZONE_CLUSTER_OUTER_RING_WIDTH,
    0,
  ];
  const colorAtRank = (rank: number): mapboxgl.ExpressionSpecification => {
    const branches = ZONE_TYPES.flatMap((type, index) => {
      // Earlier types win ties: H, then R, then UH.
      const ahead: mapboxgl.ExpressionSpecification = [
        '+',
        0,
        ...ZONE_TYPES.flatMap((other, otherIndex) => {
          if (other === type) return [];
          const comparison = otherIndex < index ? '>=' : '>';
          return [['case', [comparison, counts[other], counts[type]], 1, 0]];
        }),
      ];
      return [['all', ['>', counts[type], 0], ['==', ahead, rank]], ZONE_TYPE_COLORS[type]];
    });
    return bind(['case', ...branches, ZONE_TYPE_COLORS.H] as mapboxgl.ExpressionSpecification);
  };
  const radius = zoneClusterRadius(count);
  return {
    count,
    center: {
      'circle-radius': bind(['-', radius, ['+', innerWidth, outerWidth]]),
      'circle-color': colorAtRank(0),
      'circle-opacity': ZONE_CLUSTER_OPACITY,
      'circle-stroke-width': bind(innerWidth),
      'circle-stroke-color': colorAtRank(1),
      'circle-stroke-opacity': ZONE_CLUSTER_OPACITY,
      'circle-stroke-width-transition': { duration: 0 },
    } satisfies NonNullable<mapboxgl.CircleLayerSpecification['paint']>,
    outer: {
      'circle-radius': [
        '-',
        radius,
        ZONE_CLUSTER_OUTER_RING_WIDTH,
      ] as mapboxgl.ExpressionSpecification,
      'circle-opacity': 0,
      'circle-stroke-width': bind(outerWidth),
      'circle-stroke-color': colorAtRank(2),
      'circle-stroke-opacity': ZONE_CLUSTER_OPACITY,
      'circle-stroke-width-transition': { duration: 0 },
    } satisfies NonNullable<mapboxgl.CircleLayerSpecification['paint']>,
  };
}

/**
 * Shown until `useDealsStageFilter` resolves the per-stage dot/ring icons, and for any stage
 * HubSpot renamed or added upstream. Deal-owned on purpose: this used to be `marker-electra`, so
 * both paths drew a deal as an Electra station — a lie about the map's most important symbol, and
 * one that came and went with how fast ~25 icon registrations finished.
 *
 * A dot and a ring are different shapes, so the two roles cannot share one image.
 */
export const DEALS_FALLBACK_DOT_ICON = dealDotIconId(UNKNOWN_STAGE_STYLE.stage);
export const DEALS_FALLBACK_RING_ICON = dealRingIconId(UNKNOWN_STAGE_STYLE.stage);
const DEALS_DOT_ICON_SIZE = 1;
// The location pins render at 1.1 (useClusters); the unfolded deal matches.
const DEALS_RING_ICON_SIZE = DEAL_RING_ICON_SIZE;

const IS_CLUSTER: mapboxgl.ExpressionSpecification = ['has', 'point_count'];

function unclusteredOfKind(kind: MarkKind): mapboxgl.ExpressionSpecification {
  return ['all', ['!', IS_CLUSTER], ['==', ['get', 'kind'], kind]];
}

const CLUSTER_TEXT_FONT = ['Open Sans Bold', 'Arial Unicode MS Bold'];
/** The design's -0.01em, which Mapbox takes in ems directly. */
const CLUSTER_LETTER_SPACING = -0.01;

/**
 * The chip image and its type size, stepped off `point_count` from the one table that declares the
 * steps. A `step` expression takes the boundaries as ascending inputs, so the last step's
 * `Infinity` is the default rather than a stop.
 */
function clusterStepExpression(
  valueFor: (step: (typeof CHIP_SIZE_STEPS)[number]) => string | number,
): mapboxgl.ExpressionSpecification {
  // One stop per step after the first, each keyed on the previous step's ceiling: a step reached
  // above `9` starts at `10`. The final step's `upTo` is never read, which is why it can be
  // Infinity — and why appending a step needs nothing here.
  const [first, ...rest] = CHIP_SIZE_STEPS;
  const stops = rest.flatMap((step, index) => [CHIP_SIZE_STEPS[index].upTo + 1, valueFor(step)]);

  return [
    'step',
    ['get', 'point_count'],
    valueFor(first),
    ...stops,
  ] as mapboxgl.ExpressionSpecification;
}

function clusterChipImageExpression(): mapboxgl.ExpressionSpecification {
  return clusterStepExpression((step) => clusterChipIconId(step.px));
}

function clusterChipTextSizeExpression(): mapboxgl.ExpressionSpecification {
  return clusterStepExpression((step) => step.px);
}

/**
 * The chip's placement priority: Mapbox places the lowest key first, and a placed chip blocks the
 * ones it collides with, so negating the total keeps the biggest cluster of an overlapping pair.
 */
function clusterChipSortKeyExpression(): mapboxgl.ExpressionSpecification {
  return [
    '-',
    0,
    [
      '+',
      ['get', CLUSTER_COUNT_PROPERTY[MarkKind.LOCATION]],
      ['get', CLUSTER_COUNT_PROPERTY[MarkKind.DEAL]],
    ],
  ];
}

type ChipSection = mapboxgl.ExpressionSpecification | string;

/** Between a glyph and its own count. One constant, so both pairs are spaced alike. */
const GLYPH_GAP = ' ';
/** Between the two pairs — wider, so the counts group with their own glyph. */
const PAIR_GAP = '   ';

function countSections(kind: MarkKind): ChipSection[] {
  return [
    ['image', CLUSTER_GLYPH_ID[kind]],
    GLYPH_GAP,
    ['to-string', ['get', CLUSTER_COUNT_PROPERTY[kind]]],
  ];
}

/** A glyph and a count per kind, in one symbol. A cluster of one kind reads "3", not "3 0". */
function clusterChipTextExpression(): mapboxgl.ExpressionSpecification {
  const format = (...sections: ChipSection[]) =>
    // Each section takes its own options object; the defaults are all we want.
    ['format', ...sections.flatMap((section) => [section, {}])] as mapboxgl.ExpressionSpecification;

  const locations = countSections(MarkKind.LOCATION);
  const deals = countSections(MarkKind.DEAL);

  return [
    'case',
    ['==', ['get', CLUSTER_COUNT_PROPERTY[MarkKind.DEAL]], 0],
    format(...locations),
    ['==', ['get', CLUSTER_COUNT_PROPERTY[MarkKind.LOCATION]], 0],
    format(...deals),
    format(...locations, PAIR_GAP, ...deals),
  ] as mapboxgl.ExpressionSpecification;
}

/**
 * The unfolded pair over the open deal's source, drawn dot-then-ring so the armed mark sits
 * above the resting one it belongs to. A table so the add and the teardown iterate one list.
 */
const UNFOLDED_DEAL_MARK_LAYERS = [
  {
    layer: LayerId.SELECTED_DEAL_POINT,
    size: DEALS_DOT_ICON_SIZE,
    anchor: 'center',
    fallbackIcon: DEALS_FALLBACK_DOT_ICON,
  },
  {
    layer: LayerId.SELECTED_DEAL_RING,
    size: DEALS_RING_ICON_SIZE,
    anchor: 'bottom',
    fallbackIcon: DEALS_FALLBACK_RING_ICON,
  },
] as const satisfies readonly {
  layer: LayerId;
  size: number;
  anchor: 'center' | 'bottom';
  fallbackIcon: string;
}[];

/** One paint pass per mark kind, across both sources. */
const DEAL_DOT_LAYERS = [LayerId.DEALS_POINT, LayerId.SELECTED_DEAL_POINT] as const;
const DEAL_RING_LAYERS = [LayerId.DEALS_RING, LayerId.SELECTED_DEAL_RING] as const;

/**
 * Everything the clustered source and the mark layers' filters are recomposed from. Keyed per map,
 * not on the service instance: the service is rebuilt per effect run and several hooks write to it.
 * Filters, not feature-state — setFeatureState never repaints these symbol layers (mapbox-gl 3.22).
 */
interface MarkPresentation {
  hiddenStages: string[];
  ownerSelection: OwnerSelection;
  expandedIds: string[];
  locations: ILocation[];
  /** False drops every workspace pin. Deals stay in the shared source. */
  locationsVisible: boolean;
  selectedLocationId: string | undefined;
  /** The last mirrored set handed over, kept so a stage toggle can re-cluster it. */
  deals: IDealSummary[];
  /**
   * The deal whose card is open. Held out of the clustered source and exempt from the hidden
   * stages for as long as it is selected: a pin the user is reading never folds or vanishes.
   */
  selectedDealId: string | undefined;
}
const markPresentationByMap = new WeakMap<mapboxgl.Map, MarkPresentation>();

/** The owners whose zones and deals show, by lowercased email. Empty means no restriction. */
export type OwnerSelection = readonly string[];

// A tile omits a null attribute, so an unowned zone reads as `''` rather than null.
const zoneOwnerOrEmpty: mapboxgl.ExpressionSpecification = ['coalesce', ['get', 'owner_id'], ''];

/**
 * Membership in a cluster's pipe-wrapped lists. Exact `|<value>|`, because on a bare
 * substring bob@ matches inside rob@ and a zone name inside a longer one.
 */
export function clusterHolds(
  value: string,
  property: 'zone_names' | 'owner_ids',
): mapboxgl.ExpressionSpecification {
  return ['in', `|${value}|`, ['coalesce', ['get', property], '']];
}

/** Match the type component of country-type-name, not a code elsewhere in the name. */
function zoneTypeColor(): mapboxgl.ExpressionSpecification {
  return [
    'match',
    zoneTypeExpression(),
    'R',
    ZONE_TYPE_COLORS.R,
    'UH',
    ZONE_TYPE_COLORS.UH,
    ZONE_TYPE_COLORS.H,
  ];
}

function zoneOwnerFilter(selection: OwnerSelection): mapboxgl.ExpressionSpecification | boolean {
  if (selection.length === 0) return true;
  return ['in', zoneOwnerOrEmpty, ['literal', [...selection]]];
}

function zoneClusterOwnerFilter(
  selection: OwnerSelection,
): mapboxgl.ExpressionSpecification | boolean {
  if (selection.length === 0) return true;
  return ['any', ...selection.map((email) => clusterHolds(email, 'owner_ids'))];
}

/**
 * A cluster holding nothing but the selected zone. The selected zone already paints its own
 * highlight, so a bubble over it says "1" about the very zone the user is looking at — hidden here.
 *
 * Only the solo case: a cluster's members are counted server-side into `point_count`, so a mixed
 * cluster cannot be shown minus one member and stays as it is.
 */
function zoneClusterIsSolelySelected(zoneName: string): mapboxgl.ExpressionSpecification {
  return ['==', ['coalesce', ['get', 'zone_names'], ''], `|${zoneName}|`];
}

/**
 * Two-tier zone-emphasis hierarchy.
 *
 * When a zone is selected on the map the following visual levels apply:
 *
 * ┌────────────────────┬─────────────────┬──────────────────┬──────────────────────────┐
 * │ State              │ Fill opacity    │ Outline width    │ Outline colour           │
 * ├────────────────────┼─────────────────┼──────────────────┼──────────────────────────┤
 * │ Neighbour baseline │ 0.03            │ 1.5 px           │ brand-800 (green)        │
 * │ Selected (focused) │ 0.075           │ 4 px             │ WHITE halo               │
 * │ Selected, zoom < 8 │ —               │ —                │ hexagon pictogram        │
 * └────────────────────┴─────────────────┴──────────────────┴──────────────────────────┘
 *
 * Below ZONES_MIN_ZOOM the polygon is a speck and the cluster that held only
 * this zone is hidden, so the selection would vanish. SELECTED_ZONE_MARKER is
 * the zones pictogram — a filled hexagon with a white stroke — on the centroid,
 * and it stops where the polygon starts. The decisive cue at outline zoom is
 * the outline COLOUR, not opacity: a 0.075-vs-0.03 fill gap alone would be a
 * subtle read, so the selected zone carries a WHITE outline (SELECTED_ZONE_OUTLINE_COLOR) while
 * neighbours keep their type-colored outline. White reads unambiguously on both
 * the street and satellite basemaps and clears the non-text-contrast bar
 * against the green fill. The polygon stays in slotLayersOrder.middle, after
 * ZONES_FILL / ZONES_OUTLINE / ZONES_POINT, so it reads over neighbours and
 * under every pin. The hexagon is unslotted with the other selected marks,
 * after the cluster chip, so a low-zoom cluster cannot cover it.
 *
 * The selected highlight is independent of the ZonesOverlayControl toggle: when
 * the BD turns the overlay off, neighbours hide but the focused zone stays
 * outlined because SELECTED_ZONE_* is a separate Mapbox source+layer pair that
 * is never toggled by toggleLayerVisibility.
 */
const SELECTED_ZONE_FILL_OPACITY = 0.075;
const SELECTED_ZONE_OUTLINE_WIDTH = 4;
/**
 * The selected zone's outline is WHITE — a distinct colour from the type-colored
 * neighbour/hover outline — so the focused zone is unmistakable on any basemap
 * (the fill-opacity gap alone is too small to read). This is the canonical
 * "selected polygon" treatment in GIS tooling.
 */
const SELECTED_ZONE_OUTLINE_COLOR = '#FFFFFF';

/**
 * The AADT → colour ramp, shared by both traffic layers so they cannot drift.
 * TRAFFIC_THRESHOLDS + getTrafficByAADT stay canonical.
 * `to-number` is required: GetTrafficMVTTile writes aadt as bigint, and a raw
 * get binds that int to the float interpolate shader (GL_INVALID_OPERATION).
 */
const TRAFFIC_AADT_LINE_COLOR: mapboxgl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['to-number', ['get', 'aadt']],
  TRAFFIC_THRESHOLDS.RANGE_0_1000,
  getTrafficByAADT(TRAFFIC_THRESHOLDS.RANGE_0_1000).color,
  TRAFFIC_THRESHOLDS.RANGE_1000_8000,
  getTrafficByAADT(TRAFFIC_THRESHOLDS.RANGE_1000_8000).color,
  TRAFFIC_THRESHOLDS.RANGE_8000_15000,
  getTrafficByAADT(TRAFFIC_THRESHOLDS.RANGE_8000_15000).color,
  TRAFFIC_THRESHOLDS.RANGE_15000_44000,
  getTrafficByAADT(TRAFFIC_THRESHOLDS.RANGE_15000_44000).color,
  TRAFFIC_THRESHOLDS.RANGE_44000_PLUS,
  getTrafficByAADT(TRAFFIC_THRESHOLDS.RANGE_44000_PLUS).color,
];

/** Hairlines when zoomed out — a whole road network at once reads as a blob. */
const TRAFFIC_TILES_LINE_WIDTH: mapboxgl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  TRAFFIC_TILES_MIN_ZOOM,
  0.5,
  11,
  1.5,
  14,
  3,
  TRAFFIC_TILES_MAX_ZOOM,
  5,
];

/**
 * A composite paint property is only sampled at integer zooms, so a shorter span
 * would be inert (`mapbox-gl` `CompositeExpressionBinder`).
 */
const TRAFFIC_TILES_FADE_ZOOM_SPAN = 1;

/** Zoom each class enters the tiles at, mirroring the backend `roadClassTiers`. */
const TRAFFIC_CLASS_FADE_IN_ZOOM: Record<string, number> = {
  motorway: TRAFFIC_TILES_MIN_ZOOM,
  trunk: TRAFFIC_TILES_MIN_ZOOM,
  motorway_link: 10,
  trunk_link: 10,
  primary: 10,
  secondary: 12,
  primary_link: 12,
  tertiary: 12,
  secondary_link: 12,
  // unclassified, living_street and tertiary_link are deliberately absent: the
  // backend has no z13 tier, so they arrive with everything else and fade in at
  // TRAFFIC_CLASS_UNFILTERED_FADE_IN_ZOOM through the match's default.
};

/** Backend `unfilteredFromZoom`: where every class the table omits enters. */
const TRAFFIC_CLASS_UNFILTERED_FADE_IN_ZOOM = 14;

/**
 * A class enters fully transparent and fades up over its span. The trade-off: z14
 * is the landing zoom everywhere, so the classes entering there are invisible on
 * arrival and only reach full strength at z15.
 */
const TRAFFIC_TILES_FADE_MIN_OPACITY = 0;

const fadeProgress = (zoom: number, fadeInZoom: number): number => {
  const progress = Math.min(1, Math.max(0, (zoom - fadeInZoom) / TRAFFIC_TILES_FADE_ZOOM_SPAN));
  return TRAFFIC_TILES_FADE_MIN_OPACITY + progress * (1 - TRAFFIC_TILES_FADE_MIN_OPACITY);
};

/** A missing property means a tile built before it existed, so it must not fade out. */
const trafficTilesOpacityAt = (zoom: number): mapboxgl.ExpressionSpecification => [
  'case',
  ['has', 'road_class'],
  [
    'match',
    ['get', 'road_class'],
    ...Object.entries(TRAFFIC_CLASS_FADE_IN_ZOOM).flatMap(([roadClass, fadeInZoom]) => [
      roadClass,
      fadeProgress(zoom, fadeInZoom),
    ]),
    fadeProgress(zoom, TRAFFIC_CLASS_UNFILTERED_FADE_IN_ZOOM),
  ] as mapboxgl.ExpressionSpecification,
  fadeProgress(zoom, TRAFFIC_TILES_MIN_ZOOM),
];

/**
 * Mapbox only accepts `['zoom']` as the input of an outermost interpolate, hence
 * stops carrying the table rather than a clamp on zoom. Deduplicated after
 * expansion, not before: two entries one span apart collide, and non-ascending
 * stops throw on `addLayer`.
 */
const TRAFFIC_TILES_LINE_OPACITY: mapboxgl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  ...[
    ...new Set(
      [...Object.values(TRAFFIC_CLASS_FADE_IN_ZOOM), TRAFFIC_CLASS_UNFILTERED_FADE_IN_ZOOM].flatMap(
        (fadeInZoom) => [fadeInZoom, fadeInZoom + TRAFFIC_TILES_FADE_ZOOM_SPAN],
      ),
    ),
  ]
    .sort((left, right) => left - right)
    .flatMap((zoom) => [zoom, trafficTilesOpacityAt(zoom)]),
] as mapboxgl.ExpressionSpecification;

// Handle all layer and source management
export class MapLayersService {
  constructor(private map: MapboxMap) {}

  /**
   * `getSource` and `getLayer` reach into the map's style, which a removed map no longer has — so
   * both throw rather than answering. An unmount-time teardown reaches that state routinely: the
   * consumer's cleanup runs after the map is gone, and every HMR reload does exactly that. A map
   * that cannot be inspected holds nothing worth removing, so the answer is simply "no".
   */
  private sourceExists(sourceId: string): boolean {
    try {
      return this.map.getSource(sourceId) !== undefined;
    } catch {
      return false;
    }
  }

  private layerExists(layerId: string): boolean {
    try {
      return this.map.getLayer(layerId) !== undefined;
    } catch {
      return false;
    }
  }

  private slotLayersOrder = {
    // most data-driven layers (default)
    middle: [
      // Map-level traffic tiles — the whole road network at once, so the
      // bottom of the slot: every other layer must stay readable over it.
      // Hover still prefers traffic (layer-scoped listeners fire whatever is
      // painted above), so paint order and hover priority are independent.
      LayerId.TRAFFIC_TILES,
      // The session field sits on the roads and under the zones: it is context,
      // and the outlines have to stay readable over it. The hit circles sit
      // directly on the field so the brush is what the cursor finds.
      LayerId.COMPETITORS_HEATMAP,
      LayerId.COMPETITORS_HEATMAP_HIT,
      // Hunting zones (neighbours) overlay — background context, so it renders
      // below every other middle-slot layer, including the traffic/AADT/
      // stations/radius-circle layers fetched when a location is selected.
      LayerId.ZONES_FILL,
      LayerId.ZONES_OUTLINE,
      LayerId.ZONES_POINT,
      // The wide-zoom form of the same source. Above the outlines rather than
      // below them: the two never paint at the same zoom. A selected zone's own
      // hexagon sits just above these bubbles.
      LayerId.ZONE_CLUSTER,
      LayerId.ZONE_CLUSTER_OUTER_RING,
      LayerId.ZONE_CLUSTER_COUNT,
      // The selected zone polygon: above its neighbours so the white outline reads, and
      // otherwise the lowest thing over the roads. Everything a location or a
      // mark draws sits on top of it, never under it. The low-zoom hexagon is not
      // here: it is a selected mark, ordered with the other open pins above the clusters.
      LayerId.SELECTED_ZONE_FILL,
      LayerId.SELECTED_ZONE_OUTLINE,
      LayerId.DASHED_CIRCLE,
      LayerId.TRAFFIC_MAX_AADT,
      LayerId.CAPTURED_AADT_BORDER,
      LayerId.CAPTURED_AADT,
      LayerId.CAPTURED_AADT_MARKER,
      LayerId.MAX_AADT_CIRCLE, // On top of captured AADT routes
      LayerId.HOVER_TRAFFIC_CIRCLE,
      // The driving route the user asked for, border under line. It stays on the
      // ground: the pins it leads to are ordered after it.
      LayerId.ROUTING_BORDER,
      LayerId.ROUTING,
      // Above traffic, zones and the other ground layers. Still in this slot, so
      // locations, competitors, deals and basemap POIs paint over it.
      LayerId.PLACE_LOCATION_LINK,
      LayerId.PLACE_CURSOR_LINK,
    ],
    // Point marks. Left unslotted: Standard's top slot sits under the place
    // titles and landmark icons, and a layer with no slot is drawn after them.
    top: [
      // The dense tier is lower, so a busy station wins an overlap.
      LayerId.COMPETITORS_TILES_DENSE,
      LayerId.COMPETITORS_TILES,
      // The winning station, once a location is selected. The crown sits above
      // that pin, still under deals and the open location.
      LayerId.HIGHEST_PERFORMING_COMPETITOR_CROWN,
      // The ring sits above the dot so its hover/open state is never occluded
      // by a neighbouring resting dot.
      LayerId.DEALS_POINT,
      LayerId.DEALS_RING,
      LayerId.LOCATION_UNCLUSTERED,
      // The chip above every other summary, and above the unclustered pins so a
      // counted bubble wins the same coordinate.
      LayerId.CLUSTER_CHIP,
      // Last, so what the user has open outranks every cluster. The zone hexagon
      // is the same kind of mark: at low zoom it has to read over the chips.
      LayerId.SELECTED_ZONE_MARKER,
      LayerId.SELECTED_DEAL_POINT,
      LayerId.SELECTED_DEAL_RING,
      LayerId.SELECTED_LOCATION_MARK,
    ],
  };

  waitForLayerLoad(layerId: string) {
    return new Promise<void>((resolve) => {
      const checkLayer = () => {
        if (!this.map) throw new Error('Map is undefined');

        const layer = this.map.getLayer(layerId);
        if (layer) resolve();
        else setTimeout(checkLayer, 100);
      };

      checkLayer();
    });
  }

  orderMiddleLayers() {
    this.orderSlot(this.slotLayersOrder.middle);
    // After the ground layers, so a style without slots still paints the marks on top.
    this.orderSlot(this.slotLayersOrder.top);
  }

  private addStationModel(
    id: LayerId,
    source: SourceId,
    options?: {
      sourceLayer?: string;
      filter?: mapboxgl.FilterSpecification;
      variant?: StationModelVariant;
    },
  ) {
    if (this.layerExists(id)) return;
    this.map.addLayer(chargingStationModelLayer(id, source, this.map, options));
  }

  /** The workspace location pins and the selected one. The sources already exist. */
  addLocationStationModels() {
    this.addStationModel(LayerId.STATION_MODEL_LOCATIONS, SourceId.CLUSTERED_MARKS, {
      filter: unclusteredOfKind(MarkKind.LOCATION),
      variant: 'location',
    });
    this.addStationModel(LayerId.STATION_MODEL_SELECTED_LOCATION, SourceId.SELECTED_LOCATION, {
      variant: 'location',
    });
  }

  removeLocationStationModels() {
    for (const layerId of [
      LayerId.STATION_MODEL_LOCATIONS,
      LayerId.STATION_MODEL_SELECTED_LOCATION,
    ]) {
      if (this.layerExists(layerId)) this.map.removeLayer(layerId);
    }
  }

  private orderSlot(layerIds: readonly string[]) {
    const existing = layerIds.filter((id) => this.layerExists(id));
    for (let i = existing.length - 1; i >= 0; i--) {
      const layerId = existing[i];
      const beforeId = i === existing.length - 1 ? undefined : existing[i + 1];
      if (this.map.getLayer(layerId)) this.map.moveLayer(layerId, beforeId);
    }
  }

  // Layer visibility toggle functions
  // Array so you can toggle a group
  toggleLayerVisibility(layerIds: string[], visible: boolean) {
    layerIds.forEach((id) => {
      if (this.layerExists(id))
        this.map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    });
  }

  addDashedCircle(center: [number, number], radius: number) {
    const circle = turf.circle(center, radius, {
      steps: 128,
      units: 'kilometers',
    });
    const line = turf.polygonToLine(circle);

    // source exist, refresh data
    if (this.sourceExists(SourceId.DASHED_CIRCLE)) {
      const dashedCircleSource = this.map.getSource(
        SourceId.DASHED_CIRCLE,
      ) as mapboxgl.GeoJSONSource;
      dashedCircleSource.setData(line);
    }

    if (!this.sourceExists(SourceId.DASHED_CIRCLE)) {
      this.map.addSource(SourceId.DASHED_CIRCLE, {
        type: 'geojson',
        data: line,
      });
    }

    if (!this.layerExists(LayerId.DASHED_CIRCLE)) {
      this.map.addLayer({
        id: LayerId.DASHED_CIRCLE,
        type: 'line',
        source: SourceId.DASHED_CIRCLE,
        slot: 'middle',
        paint: {
          'line-color': '#B4ADA1',
          'line-width': 2,
          'line-dasharray': [4, 4],
        },
      });
    }
  }

  /**
   * Map-level traffic layer backed by vector tiles: the whole road network,
   * independent of any selected location. Idempotent.
   */
  addTrafficTilesLayer(tilesUrl: string) {
    if (!this.sourceExists(SourceId.TRAFFIC_TILES)) {
      this.map.addSource(SourceId.TRAFFIC_TILES, {
        type: 'vector',
        tiles: [tilesUrl],
        minzoom: TRAFFIC_TILES_MIN_ZOOM,
        maxzoom: TRAFFIC_TILES_MAX_ZOOM,
      });
    }

    if (!this.layerExists(LayerId.TRAFFIC_TILES)) {
      this.map.addLayer({
        id: LayerId.TRAFFIC_TILES,
        type: 'line',
        source: SourceId.TRAFFIC_TILES,
        'source-layer': TRAFFIC_TILES_SOURCE_LAYER,
        slot: 'middle',
        minzoom: TRAFFIC_TILES_MIN_ZOOM,
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': TRAFFIC_AADT_LINE_COLOR,
          'line-width': TRAFFIC_TILES_LINE_WIDTH,
          'line-opacity': TRAFFIC_TILES_LINE_OPACITY,
        },
      });
    }
  }

  removeTrafficTilesLayer() {
    if (this.layerExists(LayerId.TRAFFIC_TILES)) this.map.removeLayer(LayerId.TRAFFIC_TILES);
    if (this.sourceExists(SourceId.TRAFFIC_TILES)) this.map.removeSource(SourceId.TRAFFIC_TILES);
  }

  addCapturedAadtLayer(capturedAadtData: ICapturedAadtFeatureCollection) {
    const data = capturedAadtData;

    // Add/update the source
    if (this.sourceExists(SourceId.CAPTURED_AADT)) {
      const capturedAadtSource = this.map.getSource(
        SourceId.CAPTURED_AADT,
      ) as mapboxgl.GeoJSONSource;
      capturedAadtSource.setData(data);
    }
    if (!this.sourceExists(SourceId.CAPTURED_AADT)) {
      this.map.addSource(SourceId.CAPTURED_AADT, {
        type: 'geojson',
        data: data,
      });
    }

    // Add border layer (wider, darker, rendered below main line)
    if (!this.layerExists(LayerId.CAPTURED_AADT_BORDER)) {
      this.map.addLayer({
        id: LayerId.CAPTURED_AADT_BORDER,
        type: 'line',
        source: SourceId.CAPTURED_AADT,
        slot: 'middle',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          // Higher id renders on top, so segment 1 (thinner) is above segment 0
          'line-sort-key': ['id'],
        },
        paint: {
          'line-width': [
            'match',
            ['to-string', ['id']],
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.id,
            14,
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.id,
            8,
            12,
          ],
          // Border color based on feature id (darker versions)
          'line-color': [
            'match',
            ['to-string', ['id']],
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.id,
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.borderColor,
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.id,
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.borderColor,
            // Default border color
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.borderColor,
          ],
        },
      });
    }

    // Add main line layer (narrower, brighter, rendered on top of border)
    if (!this.layerExists(LayerId.CAPTURED_AADT)) {
      this.map.addLayer({
        id: LayerId.CAPTURED_AADT,
        type: 'line',
        source: SourceId.CAPTURED_AADT,
        slot: 'middle',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          // Higher id renders on top, so segment 1 (thinner) is above segment 0
          'line-sort-key': ['id'],
        },
        paint: {
          'line-width': [
            'match',
            ['to-string', ['id']],
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.id,
            7,
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.id,
            4,
            6,
          ],
          // Color based on feature id (segment 0 = cyan, segment 1 = pink)
          'line-color': [
            'match',
            ['to-string', ['id']],
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.id,
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.color,
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.id,
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.color,
            // Default color (first segment color)
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.color,
          ],
        },
      });
    }

    // Add markers at each segment's first coordinate
    this.updateCapturedAadtMarkers(data);
  }

  private updateCapturedAadtMarkers(data: ICapturedAadtFeatureCollection) {
    // Create point features for each segment's first coordinate with segment id
    const features: GeoJSON.Feature<GeoJSON.Point>[] = [];

    data.features.forEach((feature, index) => {
      const firstCoordinate = feature.geometry.coordinates[0];
      if (!firstCoordinate) return;

      features.push({
        type: 'Feature',
        id: index,
        geometry: {
          type: 'Point',
          coordinates: firstCoordinate,
        },
        properties: {
          segmentId: String(index),
        },
      });
    });

    const markerFeatures: GeoJSON.FeatureCollection<GeoJSON.Point> = {
      type: 'FeatureCollection',
      features,
    };

    // Update or create the marker source
    if (this.sourceExists(SourceId.CAPTURED_AADT_MARKER)) {
      const markerSource = this.map.getSource(
        SourceId.CAPTURED_AADT_MARKER,
      ) as mapboxgl.GeoJSONSource;
      markerSource.setData(markerFeatures);
    } else {
      this.map.addSource(SourceId.CAPTURED_AADT_MARKER, {
        type: 'geojson',
        data: markerFeatures,
      });
    }

    // Update or create the marker layer with custom icons per segment
    if (!this.layerExists(LayerId.CAPTURED_AADT_MARKER)) {
      this.map.addLayer({
        id: LayerId.CAPTURED_AADT_MARKER,
        type: 'symbol',
        source: SourceId.CAPTURED_AADT_MARKER,
        slot: 'middle',
        layout: {
          'icon-image': [
            'match',
            ['get', 'segmentId'],
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_0.id,
            'captured-aadt-marker-1',
            CAPTURED_AADT_SEGMENT_COLORS.SEGMENT_1.id,
            'captured-aadt-marker-2',
            'captured-aadt-marker-1',
          ],
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
    }
  }

  /**
   * The location's highest-AADT segments, from `max_aadt_geojson`. It owns its
   * own GeoJSON source: the endpoint returns only those segments, so nothing has
   * to be filtered out of a wider collection. Idempotent — a new location's
   * segments replace the data in place rather than re-adding the layer.
   */
  addMaxAadtLayer(data: GeoJSON.FeatureCollection) {
    try {
      if (data.features.length === 0) {
        throw new Error('No maxAadt found to be displayed');
      }

      const brandColor = getCssVariable('--max-aadt');

      if (this.sourceExists(SourceId.MAX_AADT)) {
        const source = this.map.getSource(SourceId.MAX_AADT) as mapboxgl.GeoJSONSource;
        source.setData(data);
      } else {
        this.map.addSource(SourceId.MAX_AADT, { type: 'geojson', data });
      }

      if (!this.layerExists(LayerId.TRAFFIC_MAX_AADT)) {
        this.map.addLayer({
          id: LayerId.TRAFFIC_MAX_AADT,
          type: 'line',
          source: SourceId.MAX_AADT,
          slot: 'middle',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 8, 4],
            'line-color': brandColor,
          },
        });
      }

      this.updateMaxAadtCircle(data, brandColor);
    } catch (e) {
      console.error(e);
    }
  }

  private updateMaxAadtCircle(data: GeoJSON.FeatureCollection, brandColor: string) {
    // The first segment carries the circle: with several tied segments the
    // highlight marks one of them, as it always has.
    const bbox = turf.bbox(data.features[0]);
    const bboxPolygon = turf.bboxPolygon(bbox);
    const center = turf.center(bboxPolygon);

    // Compute max distance from center to any vertex
    const exploded = turf.explode(data.features[0]);
    let maxDist = 0;
    exploded.features.forEach((pt) => {
      const dist = turf.distance(center, pt, { units: 'kilometers' });
      if (dist > maxDist) maxDist = dist;
    });

    // Create a circle that covers everything
    const boundingCircle = turf.circle(center, maxDist + MAX_AADT_CIRCLE_RADIUS_KM, {
      steps: 64,
      units: 'kilometers',
    });

    // Update or create the circle source
    if (this.sourceExists(SourceId.MAX_AADT_CIRCLE)) {
      const maxAadtCircleSource = this.map.getSource(
        SourceId.MAX_AADT_CIRCLE,
      ) as mapboxgl.GeoJSONSource;
      maxAadtCircleSource.setData(boundingCircle);
    } else {
      this.map.addSource(SourceId.MAX_AADT_CIRCLE, {
        type: 'geojson',
        data: boundingCircle,
      });
    }

    // Update or create the circle layer
    if (!this.layerExists(LayerId.MAX_AADT_CIRCLE)) {
      this.map.addLayer({
        id: LayerId.MAX_AADT_CIRCLE,
        type: 'line',
        source: SourceId.MAX_AADT_CIRCLE,
        slot: 'middle',
        paint: {
          'line-color': brandColor,
          'line-width': 3,
        },
      });
    }
  }

  /**
   * Competitor sessions as a heatmap until the zoom that draws stations, then
   * the stations themselves. One vector source: the tile changes shape with zoom.
   * Idempotent.
   */
  addCompetitorsTilesLayer(tilesUrl: string) {
    if (!this.sourceExists(SourceId.COMPETITORS_TILES)) {
      this.map.addSource(SourceId.COMPETITORS_TILES, {
        type: 'vector',
        tiles: [tilesUrl],
        minzoom: COMPETITORS_HEATMAP_MIN_ZOOM,
        maxzoom: COMPETITORS_TILES_MAX_ZOOM,
      });
    }

    if (!this.layerExists(LayerId.COMPETITORS_HEATMAP)) {
      this.map.addLayer({
        id: LayerId.COMPETITORS_HEATMAP,
        type: 'heatmap',
        source: SourceId.COMPETITORS_TILES,
        'source-layer': COMPETITORS_TILES_SOURCE_LAYER,
        slot: 'middle',
        minzoom: COMPETITORS_HEATMAP_MIN_ZOOM,
        maxzoom: COMPETITOR_HEATMAP_MAX_ZOOM,
        layout: { visibility: 'none' },
        paint: {
          'heatmap-weight': COMPETITOR_HEATMAP_WEIGHT,
          'heatmap-intensity': COMPETITOR_HEATMAP_INTENSITY,
          'heatmap-radius': COMPETITOR_HEATMAP_RADIUS,
          'heatmap-color': COMPETITOR_HEATMAP_COLOR,
          'heatmap-opacity': COMPETITOR_HEATMAP_OPACITY,
        },
      });
    }

    // Heatmap layers are not a reliable hover target. A transparent circle with
    // full opacity stays queryable. Its radius is the painted core of the brush.
    if (!this.layerExists(LayerId.COMPETITORS_HEATMAP_HIT)) {
      this.map.addLayer({
        id: LayerId.COMPETITORS_HEATMAP_HIT,
        type: 'circle',
        source: SourceId.COMPETITORS_TILES,
        'source-layer': COMPETITORS_TILES_SOURCE_LAYER,
        slot: 'middle',
        minzoom: COMPETITORS_HEATMAP_MIN_ZOOM,
        maxzoom: COMPETITOR_HEATMAP_MAX_ZOOM,
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': COMPETITOR_HEATMAP_HIT_RADIUS,
          'circle-color': 'rgba(0, 0, 0, 0)',
          'circle-opacity': 1,
        },
      });
    }

    for (const { layerId, minZoom, filter } of COMPETITOR_TIERS) {
      if (this.layerExists(layerId)) continue;

      this.map.addLayer({
        id: layerId,
        type: 'symbol',
        source: SourceId.COMPETITORS_TILES,
        'source-layer': COMPETITORS_TILES_SOURCE_LAYER,
        minzoom: minZoom,
        filter,
        layout: STATION_SYMBOL_LAYOUT,
        paint: {
          'icon-opacity': 0,
          'icon-opacity-transition': { duration: COMPETITORS_FADE_DURATION_MS, delay: 0 },
        },
      });
      this.pointerCursorOn(layerId);
    }

    this.addStationModel(LayerId.STATION_MODEL_COMPETITORS, SourceId.COMPETITORS_TILES, {
      sourceLayer: COMPETITORS_TILES_SOURCE_LAYER,
      filter: ['!=', ['get', 'cpo'], CPO_ELECTRA],
      variant: 'competitor',
    });
    this.addStationModel(LayerId.STATION_MODEL_ELECTRA, SourceId.COMPETITORS_TILES, {
      sourceLayer: COMPETITORS_TILES_SOURCE_LAYER,
      filter: ['==', ['get', 'cpo'], CPO_ELECTRA],
      variant: 'electra',
    });
  }

  /**
   * Toggles each tier across its `fadeZoom` rather than interpolating on zoom, so
   * a tier always takes the same time to cross. Reading the value back stops a
   * zoom gesture restarting the transition every frame.
   */
  syncCompetitorsTilesFade() {
    for (const { layerId, fadeZoom } of COMPETITOR_TIERS) {
      if (!this.layerExists(layerId)) continue;

      const opacity = this.map.getZoom() >= fadeZoom ? 1 : 0;
      if (this.map.getPaintProperty(layerId, 'icon-opacity') === opacity) continue;

      this.map.setPaintProperty(layerId, 'icon-opacity', opacity);
    }
  }

  removeCompetitorsTilesLayer() {
    for (const layerId of [
      LayerId.STATION_MODEL_COMPETITORS,
      LayerId.STATION_MODEL_ELECTRA,
      ...COMPETITOR_HEATMAP_LAYER_IDS,
    ]) {
      if (this.layerExists(layerId)) this.map.removeLayer(layerId);
    }
    for (const layerId of COMPETITOR_TIER_LAYER_IDS) {
      if (this.layerExists(layerId)) this.map.removeLayer(layerId);
    }
    if (this.sourceExists(SourceId.COMPETITORS_TILES))
      this.map.removeSource(SourceId.COMPETITORS_TILES);
  }

  /**
   * The source every clustering mark draws from, and the chip that marks a cluster. Idempotent,
   * and called by both the locations and the deals path: either can be first, neither owns it.
   */
  addClusteredMarksLayer() {
    if (!this.sourceExists(SourceId.CLUSTERED_MARKS)) {
      this.map.addSource(SourceId.CLUSTERED_MARKS, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: CLUSTER_RADIUS,
        clusterMinPoints: CLUSTER_MIN_POINTS,
        clusterProperties: Object.fromEntries(
          Object.values(MarkKind).map((kind) => [
            CLUSTER_COUNT_PROPERTY[kind],
            ['+', ['case', ['==', ['get', 'kind'], kind], 1, 0]],
          ]),
        ),
      });
    }

    // A symbol, not a DOM marker: Mapbox positions it from the geometry it draws from, so it never
    // drifts. `icon-text-fit` grows a stretchable background around the counts.
    if (!this.layerExists(LayerId.CLUSTER_CHIP)) {
      this.map.addLayer({
        id: LayerId.CLUSTER_CHIP,
        type: 'symbol',
        source: SourceId.CLUSTERED_MARKS,
        filter: IS_CLUSTER,
        layout: {
          'icon-image': clusterChipImageExpression(),
          'icon-text-fit': 'width',
          // Two crossing chips drew both, and every icon lands before any label: the lower chip's
          // counts ended up on the upper chip's background. Icon collision drops the whole chip.
          // Text stays out of that pass — `icon-text-fit` grows the icon after placement, and a
          // second text collision then blanks the counts while leaving the white chip.
          'icon-allow-overlap': false,
          'icon-ignore-placement': false,
          'symbol-sort-key': clusterChipSortKeyExpression(),
          'text-field': clusterChipTextExpression(),
          'text-font': CLUSTER_TEXT_FONT,
          'text-size': clusterChipTextSizeExpression(),
          'text-letter-spacing': CLUSTER_LETTER_SPACING,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          // Sits above its coordinate instead of on it, like the location pins, so it stops
          // landing on the zone bubble. `icon-text-fit` makes the text anchor drive both.
          'text-anchor': 'bottom',
          'icon-anchor': 'bottom',
        },
        paint: { 'text-color': CHIP_COUNT_COLOR },
      });
      this.pointerCursorOn(LayerId.CLUSTER_CHIP);
    }
  }

  /**
   * The deals' own marks over the shared source: `DEALS_POINT` the resting dot, `DEALS_RING` the
   * hover/open ring, swapped through `setDealExpanded`. The data arrives through `setDealsData`.
   */
  addDealsLayer() {
    this.addClusteredMarksLayer();

    if (!this.layerExists(LayerId.DEALS_POINT)) {
      this.map.addLayer({
        id: LayerId.DEALS_POINT,
        type: 'symbol',
        source: SourceId.CLUSTERED_MARKS,
        layout: {
          'icon-image': DEALS_FALLBACK_DOT_ICON,
          'icon-size': DEALS_DOT_ICON_SIZE,
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      this.pointerCursorOn(LayerId.DEALS_POINT);
    }

    if (!this.layerExists(LayerId.DEALS_RING)) {
      this.map.addLayer({
        id: LayerId.DEALS_RING,
        type: 'symbol',
        source: SourceId.CLUSTERED_MARKS,
        layout: {
          'icon-image': DEALS_FALLBACK_RING_ICON,
          'icon-size': DEALS_RING_ICON_SIZE,
          // The ring's tip touches the same coordinate as the dot's center.
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      this.pointerCursorOn(LayerId.DEALS_RING);
    }

    // The open deal, held apart so it is never folded into a cluster.
    if (!this.sourceExists(SourceId.SELECTED_DEAL)) {
      this.map.addSource(SourceId.SELECTED_DEAL, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    // Neither carries a filter: the open deal keeps its dot and wears the armed mark above it,
    // whatever is expanded on the clustered source.
    for (const { layer, size, anchor, fallbackIcon } of UNFOLDED_DEAL_MARK_LAYERS) {
      if (this.layerExists(layer)) continue;
      this.map.addLayer({
        id: layer,
        type: 'symbol',
        source: SourceId.SELECTED_DEAL,
        layout: {
          'icon-image': fallbackIcon,
          'icon-size': size,
          'icon-anchor': anchor,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
      this.pointerCursorOn(layer);
    }

    this.addStationModel(LayerId.STATION_MODEL_DEALS, SourceId.CLUSTERED_MARKS, {
      filter: unclusteredOfKind(MarkKind.DEAL),
      variant: 'deal',
    });
    this.addStationModel(LayerId.STATION_MODEL_SELECTED_DEALS, SourceId.SELECTED_DEAL, {
      variant: 'deal',
    });

    this.applyDealsFilters();
  }

  /**
   * Replaces the mirrored set; the sources re-index, which is what a refreshed read costs.
   * A selected zone does not narrow this set: every deal stays on the map.
   */
  setDealsData(deals: IDealSummary[]) {
    const presentation = this.markPresentation();
    presentation.deals = deals;
    this.applyClusteredData();
  }

  /** Replaces the workspace's locations; they cluster alongside the deals. */
  setLocationsData(locations: ILocation[]) {
    this.markPresentation().locations = locations;
    this.applyClusteredData();
  }

  /**
   * Shows or hides the workspace pins. They leave both sources, so a cluster
   * recounts without them. Deals stay. The open location's card is not this layer.
   */
  setLocationsVisible(visible: boolean) {
    const presentation = this.markPresentation();
    if (presentation.locationsVisible === visible) return;
    presentation.locationsVisible = visible;
    this.applyClusteredData();
  }

  /** Held out of the clustered source so the open pin is never absorbed. Null folds it back in. */
  setSelectedLocation(locationId: string | null) {
    this.markPresentation().selectedLocationId = locationId ?? undefined;
    this.applyClusteredData();
  }

  /**
   * The single writer of all three sources: one shared source holds two kinds fed by two callers,
   * so no caller assembles its data. Clustering then runs on what is left.
   */
  private applyClusteredData() {
    const {
      locations,
      locationsVisible,
      selectedLocationId,
      deals,
      hiddenStages,
      ownerSelection,
      selectedDealId,
    } = this.markPresentation();

    const selectedDeals: IDealSummary[] = [];
    const clusteredDeals: IDealSummary[] = [];
    for (const deal of deals) {
      if (deal.id === selectedDealId) {
        selectedDeals.push(deal);
        continue;
      }
      clusteredDeals.push(deal);
    }

    const shownLocations = locationsVisible ? locations : [];
    const selectedLocations = shownLocations.filter(
      (location) => location.id === selectedLocationId,
    );
    const clusteredLocations = shownLocations.filter(
      (location) => location.id !== selectedLocationId,
    );

    // The selected deal is fed past the hidden-stage and owner filters: the card being read keeps
    // its pin.
    this.setGeoJSONData(
      SourceId.CLUSTERED_MARKS,
      toClusteredMarkFeatures({
        locations: clusteredLocations,
        deals: clusteredDeals,
        hiddenStages,
        ownerSelection,
      }),
    );
    this.setGeoJSONData(SourceId.SELECTED_DEAL, toDealFeatureCollection(selectedDeals));
    this.setGeoJSONData(
      SourceId.SELECTED_LOCATION,
      toClusteredMarkFeatures({ locations: selectedLocations }),
    );
  }

  private setGeoJSONData(sourceId: SourceId, data: GeoJSON.FeatureCollection) {
    const source = this.map.getSource(sourceId);
    if (source?.type !== 'geojson') return;
    source.setData(data);
  }

  removeDealsLayer() {
    // Reverse of the add order, so each ring goes before its dot and each source after its layers.
    if (this.layerExists(LayerId.STATION_MODEL_SELECTED_DEALS))
      this.map.removeLayer(LayerId.STATION_MODEL_SELECTED_DEALS);
    for (const { layer } of [...UNFOLDED_DEAL_MARK_LAYERS].reverse()) {
      if (this.layerExists(layer)) this.map.removeLayer(layer);
    }
    if (this.sourceExists(SourceId.SELECTED_DEAL)) this.map.removeSource(SourceId.SELECTED_DEAL);
    if (this.layerExists(LayerId.STATION_MODEL_DEALS))
      this.map.removeLayer(LayerId.STATION_MODEL_DEALS);
    if (this.layerExists(LayerId.DEALS_RING)) this.map.removeLayer(LayerId.DEALS_RING);
    if (this.layerExists(LayerId.DEALS_POINT)) this.map.removeLayer(LayerId.DEALS_POINT);
    // The shared source stays — dropping it would take the workspace's pins with the mirror.
    const presentation = this.markPresentation();
    presentation.deals = [];
    presentation.selectedDealId = undefined;
    presentation.expandedIds = [];
    this.applyClusteredData();
  }

  /** Paints both mark layers per stage; the icon ids must already be registered images. */
  paintDealsMarks(dotIdByStage: Record<string, string>, ringIdByStage: Record<string, string>) {
    if (!this.layerExists(LayerId.DEALS_POINT) || !this.layerExists(LayerId.DEALS_RING)) return;

    const stages = Object.keys(dotIdByStage);
    if (stages.length === 0) return;

    const matchExpression = (
      iconIdByStage: Record<string, string>,
      fallback: string,
    ): mapboxgl.ExpressionSpecification => [
      'match',
      ['get', 'stage'],
      ...stages.flatMap((stage) => [stage, iconIdByStage[stage]]),
      fallback,
    ];
    for (const layer of DEAL_DOT_LAYERS) {
      if (this.layerExists(layer)) {
        const expression = matchExpression(dotIdByStage, DEALS_FALLBACK_DOT_ICON);
        this.map.setLayoutProperty(layer, 'icon-image', expression);
      }
    }
    for (const layer of DEAL_RING_LAYERS) {
      if (this.layerExists(layer)) {
        const expression = matchExpression(ringIdByStage, DEALS_FALLBACK_RING_ICON);
        this.map.setLayoutProperty(layer, 'icon-image', expression);
      }
    }
  }

  private markPresentation(): MarkPresentation {
    let presentation = markPresentationByMap.get(this.map);
    if (!presentation) {
      presentation = {
        hiddenStages: [],
        ownerSelection: [],
        expandedIds: [],
        locations: [],
        locationsVisible: true,
        selectedLocationId: undefined,
        deals: [],
        selectedDealId: undefined,
      };
      markPresentationByMap.set(this.map, presentation);
    }
    return presentation;
  }

  /** Recomposes both mark layers' filters from the whole presentation. */
  private applyDealsFilters() {
    if (!this.layerExists(LayerId.DEALS_POINT) || !this.layerExists(LayerId.DEALS_RING)) return;

    const { expandedIds } = this.markPresentation();
    const isExpanded: mapboxgl.ExpressionSpecification = [
      'in',
      ['get', 'id'],
      ['literal', expandedIds],
    ];

    // No stage predicate: a hidden stage is absent from the source, not filtered out of the layer.
    const unclusteredDeal = unclusteredOfKind(MarkKind.DEAL);
    this.map.setFilter(LayerId.DEALS_POINT, unclusteredDeal);
    this.map.setFilter(LayerId.DEALS_RING, ['all', unclusteredDeal, isExpanded]);
  }

  /** Hides the given stages; an empty list shows every stage. The source re-clusters on what is left. */
  hideDealStages(hiddenStages: string[]) {
    this.markPresentation().hiddenStages = hiddenStages;
    this.applyClusteredData();
  }

  filterDealOwners(selection: OwnerSelection) {
    this.markPresentation().ownerSelection = selection;
    this.applyClusteredData();
  }

  /** The hidden stages as last applied; empty meaning every stage shows. */
  dealsHiddenStages(): string[] {
    return this.markPresentation().hiddenStages;
  }

  /** Swaps one deal between the resting dot and the hover/open ring. No-ops before the layers exist. */
  setDealExpanded(featureId: string | number, expanded: boolean) {
    const presentation = this.markPresentation();
    const id = String(featureId);
    presentation.expandedIds = expanded
      ? [...new Set([...presentation.expandedIds, id])]
      : presentation.expandedIds.filter((expandedId) => expandedId !== id);
    this.applyDealsFilters();
  }

  /**
   * Marks the deal whose card is open, or none. The selected deal moves to the unfolded source and
   * ignores the hidden stages until it is deselected; its ring follows the selection, so a deal
   * folding back into the clusters folds its ring too.
   */
  setSelectedDeal(dealId: string | undefined) {
    const presentation = this.markPresentation();
    const previous = presentation.selectedDealId;
    if (previous === dealId) return;

    presentation.selectedDealId = dealId;
    this.applyClusteredData();
    if (previous !== undefined) this.setDealExpanded(previous, false);
    if (dealId !== undefined) this.setDealExpanded(dealId, true);
  }

  /**
   * Restores the cursor an armed tool owns (`crosshair`) rather than clearing it.
   *
   * The saved value lives on the canvas rather than in a closure: the service is
   * rebuilt per effect run and these handlers are never unwired, so two snapshots
   * would restore each other's value and strand the canvas on `pointer`.
   */
  private pointerCursorOn(layerId: LayerId) {
    this.map.on('mouseenter', layerId, () => {
      const canvas = this.map.getCanvas();
      if (canvas.dataset.cursorBeforeHover === undefined) {
        canvas.dataset.cursorBeforeHover = canvas.style.cursor;
      }
      canvas.style.cursor = 'pointer';
    });

    this.map.on('mouseleave', layerId, () => {
      const canvas = this.map.getCanvas();
      const cursorBeforeHover = canvas.dataset.cursorBeforeHover;
      // Already restored by another registration of this pair — restoring again
      // would clear the cursor the armed tool owns.
      if (cursorBeforeHover === undefined) return;

      canvas.style.cursor = cursorBeforeHover;
      delete canvas.dataset.cursorBeforeHover;
    });
  }

  /**
   * Render the hunting-zones overlay from vector tiles
   * (`GET /tiles/{z}/{x}/{y}?layer=zones`). One vector source plus six layers
   * in the slot order already wired below every other middle-slot layer: a
   * translucent type-colored fill, a solid outline, a defensive circle for any
   * Point/MultiPoint geometry (all current zones are Polygons, so the circle
   * layer is defensive only), and the cluster center + outer ring + count that replace all
   * three below `ZONES_MIN_ZOOM`.
   *
   * The two forms come from the same endpoint: the backend aggregates zones into
   * counted points below the outline zooms, so which form a tile carries is its
   * zoom's business, not a second source or a second request.
   *
   * Individual zones are colored by their H/R/UH name component, independent
   * of ownership. Clusters rank their visible type counts: the largest fills
   * the center, with up to two fixed-width rings for the other types.
   *
   * Idempotent: re-invoking never re-adds the source or the layers, so a pan or
   * zoom costs nothing here — Mapbox requests the tiles it needs itself.
   */
  addZonesTilesLayer(tilesUrl: string) {
    const zoneColor = zoneTypeColor();
    const clusterPaint = zoneClusterPaint([]);

    if (!this.sourceExists(SourceId.ZONES)) {
      this.map.addSource(SourceId.ZONES, {
        type: 'vector',
        tiles: [tilesUrl],
        minzoom: ZONE_CLUSTERS_MIN_ZOOM,
        maxzoom: ZONES_TILES_MAX_ZOOM,
      });
    }

    if (!this.layerExists(LayerId.ZONES_FILL)) {
      this.map.addLayer({
        id: LayerId.ZONES_FILL,
        type: 'fill',
        source: SourceId.ZONES,
        'source-layer': ZONES_TILES_SOURCE_LAYER,
        slot: 'middle',
        minzoom: ZONES_MIN_ZOOM,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'fill-color': zoneColor,
          'fill-opacity': ZONES_FILL_OPACITY,
        },
      });
    }

    if (!this.layerExists(LayerId.ZONES_OUTLINE)) {
      this.map.addLayer({
        id: LayerId.ZONES_OUTLINE,
        type: 'line',
        source: SourceId.ZONES,
        'source-layer': ZONES_TILES_SOURCE_LAYER,
        slot: 'middle',
        minzoom: ZONES_MIN_ZOOM,
        filter: ['==', ['geometry-type'], 'Polygon'],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': zoneColor,
          'line-width': ZONES_OUTLINE_WIDTH,
          'line-opacity': ZONES_OUTLINE_OPACITY,
        },
      });
    }

    // Defensive: render Point/MultiPoint zones as circles. The Notion sync
    // parser rejects non-Polygon geometry today, so this never fires in
    // practice — it guards against future geometry types.
    if (!this.layerExists(LayerId.ZONES_POINT)) {
      this.map.addLayer({
        id: LayerId.ZONES_POINT,
        type: 'circle',
        source: SourceId.ZONES,
        'source-layer': ZONES_TILES_SOURCE_LAYER,
        slot: 'middle',
        minzoom: ZONES_MIN_ZOOM,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': ZONES_POINT_RADIUS,
          'circle-color': zoneColor,
          'circle-opacity': ZONES_POINT_OPACITY,
          'circle-stroke-color': zoneColor,
          'circle-stroke-width': ZONES_OUTLINE_WIDTH,
          'circle-stroke-opacity': ZONES_OUTLINE_OPACITY,
        },
      });
    }

    // The dominant type fills the center; second/third types use fixed-width rings.
    if (!this.layerExists(LayerId.ZONE_CLUSTER)) {
      this.map.addLayer({
        id: LayerId.ZONE_CLUSTER,
        type: 'circle',
        source: SourceId.ZONES,
        'source-layer': ZONES_TILES_SOURCE_LAYER,
        slot: 'middle',
        minzoom: ZONE_CLUSTERS_MIN_ZOOM,
        maxzoom: ZONES_MIN_ZOOM,
        filter: ['has', 'point_count'],
        paint: clusterPaint.center,
      });
    }

    if (!this.layerExists(LayerId.ZONE_CLUSTER_OUTER_RING)) {
      this.map.addLayer({
        id: LayerId.ZONE_CLUSTER_OUTER_RING,
        type: 'circle',
        source: SourceId.ZONES,
        'source-layer': ZONES_TILES_SOURCE_LAYER,
        slot: 'middle',
        minzoom: ZONE_CLUSTERS_MIN_ZOOM,
        maxzoom: ZONES_MIN_ZOOM,
        filter: ['has', 'point_count'],
        paint: clusterPaint.outer,
      });
    }

    if (!this.layerExists(LayerId.ZONE_CLUSTER_COUNT)) {
      this.map.addLayer({
        id: LayerId.ZONE_CLUSTER_COUNT,
        type: 'symbol',
        source: SourceId.ZONES,
        'source-layer': ZONES_TILES_SOURCE_LAYER,
        slot: 'middle',
        minzoom: ZONE_CLUSTERS_MIN_ZOOM,
        maxzoom: ZONES_MIN_ZOOM,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['to-string', ['get', 'point_count']],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': ZONE_CLUSTER_LABEL_SIZE,
          'text-anchor': 'center',
          // Out of placement entirely: a symbol Mapbox thinks collided is cross-faded,
          // which reads as the label pulsing on a pan.
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': ZONE_CLUSTER_LABEL_COLOR,
          // Mapbox transitions paint over 300ms by default — the other half of the fade.
          'text-opacity': 1,
          'text-opacity-transition': { duration: 0, delay: 0 },
        },
      });
    }

    this.paintZoneColors(zoneColor);
    this.paintZoneClusters(clusterPaint);
  }

  /**
   * Re-apply type colors to existing territory layers.
   */
  private paintZoneColors(zoneColor: string | mapboxgl.ExpressionSpecification) {
    if (this.layerExists(LayerId.ZONES_FILL)) {
      this.map.setPaintProperty(LayerId.ZONES_FILL, 'fill-color', zoneColor);
    }
    if (this.layerExists(LayerId.ZONES_OUTLINE)) {
      this.map.setPaintProperty(LayerId.ZONES_OUTLINE, 'line-color', zoneColor);
    }
    if (this.layerExists(LayerId.ZONES_POINT)) {
      this.map.setPaintProperty(LayerId.ZONES_POINT, 'circle-color', zoneColor);
      this.map.setPaintProperty(LayerId.ZONES_POINT, 'circle-stroke-color', zoneColor);
    }
  }

  private paintZoneClusters(presentation: ReturnType<typeof zoneClusterPaint>) {
    for (const [layerId, paint] of [
      [LayerId.ZONE_CLUSTER, presentation.center],
      [LayerId.ZONE_CLUSTER_OUTER_RING, presentation.outer],
    ] as const) {
      if (!this.layerExists(layerId)) continue;
      for (const [property, value] of Object.entries(paint))
        this.map.setPaintProperty(
          layerId,
          property as keyof NonNullable<mapboxgl.CircleLayerSpecification['paint']>,
          value,
        );
    }
    if (this.layerExists(LayerId.ZONE_CLUSTER_COUNT)) {
      this.map.setLayoutProperty(LayerId.ZONE_CLUSTER_COUNT, 'text-field', [
        'to-string',
        presentation.count,
      ]);
    }
  }

  /**
   * Tear down the hunting-zones overlay — layers first, then the source.
   * Called when the overlay is disabled or on project switch/unmount so the
   * next project doesn't inherit the previous project's zones.
   */
  removeZonesLayer() {
    for (const layerId of [
      LayerId.ZONES_FILL,
      LayerId.ZONES_OUTLINE,
      LayerId.ZONES_POINT,
      LayerId.ZONE_CLUSTER,
      LayerId.ZONE_CLUSTER_OUTER_RING,
      LayerId.ZONE_CLUSTER_COUNT,
    ]) {
      if (this.layerExists(layerId)) this.map.removeLayer(layerId);
    }
    if (this.sourceExists(SourceId.ZONES)) this.map.removeSource(SourceId.ZONES);
  }

  /**
   * Narrow the zones overlay to the selected owners and types. A filter rather than a layer set:
   * one source, one paint pass, and no remove/re-add cycle when the selection changes. Runs after
   * `addZonesLayer` (mirrors `applyTrafficTilesVisibility`) and is orthogonal to the master
   * overlay toggle, which stays on `toggleLayerVisibility`.
   */
  filterZones(
    selection: OwnerSelection,
    types: readonly ZoneType[] = ZONE_TYPES,
    selectedZoneName?: string,
  ) {
    const ownership = zoneOwnerFilter(selection);
    const typeFilter: mapboxgl.ExpressionSpecification | boolean = ZONE_TYPES.every((type) =>
      types.includes(type),
    )
      ? true
      : ['in', zoneTypeExpression(), ['literal', [...types]]];

    for (const [layerId, geometryType] of [
      [LayerId.ZONES_FILL, 'Polygon'],
      [LayerId.ZONES_OUTLINE, 'Polygon'],
      [LayerId.ZONES_POINT, 'Point'],
    ] as const) {
      if (!this.layerExists(layerId)) continue;
      const filter: unknown[] = ['all', ['==', ['geometry-type'], geometryType], ownership];
      if (typeFilter !== true) filter.push(typeFilter);
      this.map.setFilter(layerId, filter as mapboxgl.FilterSpecification);
    }

    // Selection remains exempt from the owner filter, but a hidden type must not
    // reappear through the highlight or its low-zoom mark.
    for (const [layerId, geometryType] of [
      [LayerId.SELECTED_ZONE_FILL, 'Polygon'],
      [LayerId.SELECTED_ZONE_OUTLINE, 'Polygon'],
      [LayerId.SELECTED_ZONE_MARKER, 'Point'],
    ] as const) {
      if (this.layerExists(layerId)) {
        this.map.setFilter(layerId, ['all', ['==', ['geometry-type'], geometryType], typeFilter]);
      }
    }

    // The cluster form of the same selection, so the wide zooms obey the control too.
    const clusterOwnership = zoneClusterOwnerFilter(selection);
    const clusterPaint = zoneClusterPaint(selection, types);
    // The exclusion is only appended when there is a selection, so the filter a plain toggle
    // produces keeps the exact shape it had.
    const clusterFilter: unknown[] = ['all', ['has', 'point_count'], clusterOwnership];
    if (typeFilter !== true) clusterFilter.push(['>', clusterPaint.count, 0]);
    if (selectedZoneName != null) {
      clusterFilter.push(['!', zoneClusterIsSolelySelected(selectedZoneName)]);
    }
    for (const layerId of [
      LayerId.ZONE_CLUSTER,
      LayerId.ZONE_CLUSTER_OUTER_RING,
      LayerId.ZONE_CLUSTER_COUNT,
    ]) {
      if (!this.layerExists(layerId)) continue;
      this.map.setFilter(layerId, clusterFilter as mapboxgl.FilterSpecification);
    }

    this.paintZoneClusters(clusterPaint);
  }

  /**
   * Highlight the single focused/selected zone on the map. Renders a
   * dedicated source + fill + outline drawn on top of the zones overlay so the
   * selected zone stands out and neighbours read as de-emphasised by contrast.
   *
   * Idempotent: re-invoking refreshes the source via `setData` (so switching
   * zones moves the highlight without a remove/re-add cycle) and never re-adds
   * the layers.
   *
   * Paint: a type-colored fill at SELECTED_ZONE_FILL_OPACITY plus
   * a WHITE outline (SELECTED_ZONE_OUTLINE_COLOR) — the white halo is the cue
   * that distinguishes the selected zone from a hovered neighbour. See the
   * three-tier hierarchy table at the top of this file. Both start at
   * ZONES_MIN_ZOOM. Below that, the zones pictogram — a filled hexagon with a
   * white stroke — stands on the centroid: the polygon is too small to find,
   * and the cluster that held only this zone is hidden.
   */
  addSelectedZoneHighlight(zone: { geometry: IGeoJSONGeometry; name?: string }) {
    // Wrap the zone geometry into a single-feature collection. Boundary cast:
    // IGeoJSONGeometry is structurally identical to GeoJSON.Geometry on the
    // wire but not assignable without help (same pattern as addZonesLayer).
    // The mark is a second feature on the same source so one setData moves
    // both the polygon and the low-zoom mark. pointOnFeature, not centroid: a
    // concave or multi-part zone has its centroid off the shape.
    const geometry = zone.geometry as unknown as GeoJSON.Geometry;
    const features: GeoJSON.Feature[] = [
      {
        type: 'Feature',
        geometry,
        properties: { name: zone.name ?? '', zone_type: zoneTypeFromName(zone.name) },
      },
    ];
    if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
      features.push({ ...turf.pointOnFeature(geometry), properties: features[0].properties });
    }
    const data: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    if (this.sourceExists(SourceId.SELECTED_ZONE)) {
      const source = this.map.getSource(SourceId.SELECTED_ZONE) as mapboxgl.GeoJSONSource;
      source.setData(data);
    } else {
      this.map.addSource(SourceId.SELECTED_ZONE, { type: 'geojson', data });
    }

    if (!this.layerExists(LayerId.SELECTED_ZONE_FILL)) {
      this.map.addLayer({
        id: LayerId.SELECTED_ZONE_FILL,
        type: 'fill',
        source: SourceId.SELECTED_ZONE,
        slot: 'middle',
        minzoom: ZONES_MIN_ZOOM,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'fill-color': zoneTypeColor(),
          'fill-opacity': SELECTED_ZONE_FILL_OPACITY,
        },
      });
    }

    if (!this.layerExists(LayerId.SELECTED_ZONE_OUTLINE)) {
      this.map.addLayer({
        id: LayerId.SELECTED_ZONE_OUTLINE,
        type: 'line',
        source: SourceId.SELECTED_ZONE,
        slot: 'middle',
        minzoom: ZONES_MIN_ZOOM,
        filter: ['==', ['geometry-type'], 'Polygon'],
        layout: { 'line-join': 'round' },
        paint: {
          // White halo (not the type color) so the selected zone is distinct from a
          // hovered neighbour on any basemap — see the SELECTED_ZONE_OUTLINE_COLOR note.
          'line-color': SELECTED_ZONE_OUTLINE_COLOR,
          'line-width': SELECTED_ZONE_OUTLINE_WIDTH,
        },
      });
    }

    if (!this.layerExists(LayerId.SELECTED_ZONE_MARKER)) {
      this.map.addLayer({
        id: LayerId.SELECTED_ZONE_MARKER,
        type: 'symbol',
        source: SourceId.SELECTED_ZONE,
        maxzoom: ZONES_MIN_ZOOM,
        filter: ['==', ['geometry-type'], 'Point'],
        layout: {
          'icon-image': ['concat', `${SELECTED_ZONE_HEX_ICON}-`, zoneTypeExpression()],
          'icon-size': 1,
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
    }

    // The highlight is added long after the other ground layers, so Mapbox stacks
    // it on top of the route and the neighbour zones. Ordering pulls the marks
    // back above it. They stay unslotted so they also clear the basemap labels.
    this.orderMiddleLayers();
  }

  /**
   * Tear down the selected-zone highlight — layers first, then the source.
   * Called when no zone is selected or on unmount so a later view doesn't
   * inherit a stale highlight.
   */
  removeSelectedZoneHighlight() {
    for (const layerId of [
      LayerId.SELECTED_ZONE_FILL,
      LayerId.SELECTED_ZONE_OUTLINE,
      LayerId.SELECTED_ZONE_MARKER,
    ]) {
      if (this.layerExists(layerId)) this.map.removeLayer(layerId);
    }
    if (this.sourceExists(SourceId.SELECTED_ZONE)) this.map.removeSource(SourceId.SELECTED_ZONE);
  }

  addRouting(routingResponse: IMapboxDirectionsResponse) {
    const routes = routingResponse.routes;

    if (this.sourceExists(SourceId.ROUTING)) {
      const routingSource = this.map.getSource(SourceId.ROUTING) as mapboxgl.GeoJSONSource;
      routingSource.setData(routes[0].geometry);
    } else {
      this.map.addSource(SourceId.ROUTING, {
        type: 'geojson',
        data: routes[0].geometry,
      });
    }

    // Add border layer (wider, background)
    if (!this.layerExists(LayerId.ROUTING_BORDER)) {
      this.map.addLayer({
        id: LayerId.ROUTING_BORDER,
        type: 'line',
        source: SourceId.ROUTING,
        slot: 'middle',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 11, 12],
          'line-color': '#2E2EFB',
        },
      });
    }

    // Add main line layer (narrower, foreground)
    if (!this.layerExists(LayerId.ROUTING)) {
      this.map.addLayer({
        id: LayerId.ROUTING,
        type: 'line',
        source: SourceId.ROUTING,
        slot: 'middle',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 7, 7],
          'line-color': '#B7C9FE',
        },
      });
    }

    // Added on demand, long after the rest of the slot — same lazy path as the
    // selected-zone highlight, so it orders itself for the same reason.
    this.orderMiddleLayers();
  }

  removeRouting() {
    if (this.layerExists(LayerId.ROUTING_BORDER)) {
      this.map.removeLayer(LayerId.ROUTING_BORDER);
    }
    if (this.layerExists(LayerId.ROUTING)) {
      this.map.removeLayer(LayerId.ROUTING);
    }
    if (this.sourceExists(SourceId.ROUTING)) {
      this.map.removeSource(SourceId.ROUTING);
    }
  }

  /**
   * Tear down every per-location detail layer added by `addDashedCircle`,
   * `addMaxAadtLayer` and `addCapturedAadtLayer`, plus the hover cursor
   * circle those layers share — layers first, then their sources.
   */
  removeLocationDetailLayers() {
    if (this.layerExists(LayerId.CAPTURED_AADT_MARKER)) {
      this.map.removeLayer(LayerId.CAPTURED_AADT_MARKER);
    }
    if (this.layerExists(LayerId.CAPTURED_AADT)) this.map.removeLayer(LayerId.CAPTURED_AADT);
    if (this.layerExists(LayerId.CAPTURED_AADT_BORDER)) {
      this.map.removeLayer(LayerId.CAPTURED_AADT_BORDER);
    }
    if (this.sourceExists(SourceId.CAPTURED_AADT_MARKER)) {
      this.map.removeSource(SourceId.CAPTURED_AADT_MARKER);
    }
    if (this.sourceExists(SourceId.CAPTURED_AADT)) this.map.removeSource(SourceId.CAPTURED_AADT);

    if (this.layerExists(LayerId.HOVER_TRAFFIC_CIRCLE)) {
      this.map.removeLayer(LayerId.HOVER_TRAFFIC_CIRCLE);
    }
    if (this.sourceExists(SourceId.HOVER_TRAFFIC_CIRCLE)) {
      this.map.removeSource(SourceId.HOVER_TRAFFIC_CIRCLE);
    }

    if (this.layerExists(LayerId.MAX_AADT_CIRCLE)) this.map.removeLayer(LayerId.MAX_AADT_CIRCLE);
    if (this.sourceExists(SourceId.MAX_AADT_CIRCLE)) {
      this.map.removeSource(SourceId.MAX_AADT_CIRCLE);
    }

    if (this.layerExists(LayerId.TRAFFIC_MAX_AADT)) this.map.removeLayer(LayerId.TRAFFIC_MAX_AADT);
    if (this.sourceExists(SourceId.MAX_AADT)) this.map.removeSource(SourceId.MAX_AADT);

    if (this.layerExists(LayerId.DASHED_CIRCLE)) this.map.removeLayer(LayerId.DASHED_CIRCLE);
    if (this.sourceExists(SourceId.DASHED_CIRCLE)) this.map.removeSource(SourceId.DASHED_CIRCLE);
  }

  /**
   * Crowns the winning competitor station.
   *
   * Stations are vector tiles, so this is a point rather than feature-state
   * on a tile feature. The point follows the tile's own coordinate: past zoom
   * 12 those tiles are only stretched, and the stored coordinate walks off
   * the pin. The crown uses the zoom and the fade of the tier that would draw
   * this station: the main tier from zoom 10 for high performers or sparse
   * neighborhoods, otherwise the dense tier from 12. A later call moves the
   * point and retargets that gate.
   */
  showHighestPerformingCompetitor(longitude: number, latitude: number, performance: number) {
    highestCompetitorTarget.set(this.map, { longitude, latitude, performance });
    const minZoom =
      this.snapHighestPerformingCompetitorToTile() ?? highestCompetitorMinZoom(performance);

    if (!this.layerExists(LayerId.HIGHEST_PERFORMING_COMPETITOR_CROWN)) {
      this.map.addLayer({
        id: LayerId.HIGHEST_PERFORMING_COMPETITOR_CROWN,
        type: 'symbol',
        source: SourceId.HIGHEST_PERFORMING_COMPETITOR,
        minzoom: minZoom,
        layout: {
          'icon-image': HIGHEST_COMPETITOR_CROWN_ICON,
          'icon-size': 1,
          'icon-anchor': 'bottom',
          'icon-pitch-alignment': 'viewport',
          'icon-rotation-alignment': 'viewport',
          // Mapbox applies this offset in pixels, not ems. Anchor bottom sits
          // the crown on the coordinate. -13 sets the base on the 36px pin.
          'icon-offset': [0, HIGHEST_COMPETITOR_CROWN_OFFSET_Y],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
        paint: {
          'icon-opacity': 0,
          'icon-opacity-transition': { duration: COMPETITORS_FADE_DURATION_MS, delay: 0 },
        },
      });
    } else {
      this.map.setLayerZoomRange(
        LayerId.HIGHEST_PERFORMING_COMPETITOR_CROWN,
        minZoom,
        HIGHEST_COMPETITOR_MARK_MAX_ZOOM,
      );
    }

    highestCompetitorFadeZoom.set(this.map, minZoom + COMPETITORS_FADE_LEAD_ZOOM);
    bindHighestCompetitorFade(this.map);
    bindHighestCompetitorSnap(this.map);
    this.syncHighestPerformingCompetitorFade();
    this.orderMiddleLayers();
  }

  /**
   * Moves the crown onto the loaded tile station. A finer tile replaces the
   * stretched one as the map zooms, so the snap has to run again.
   */
  snapHighestPerformingCompetitorToTile() {
    const target = highestCompetitorTarget.get(this.map);
    if (!target) return;

    const station = this.snappedTileStation(target.longitude, target.latitude, target.performance);
    const [longitude, latitude] =
      station?.geometry.type === 'Point'
        ? station.geometry.coordinates
        : [target.longitude, target.latitude];
    const minZoom = highestCompetitorMinZoom(
      target.performance,
      station?.properties?.nearby_hpc_count,
    );
    const fadeZoom = minZoom + COMPETITORS_FADE_LEAD_ZOOM;
    if (highestCompetitorFadeZoom.get(this.map) !== fadeZoom) {
      highestCompetitorFadeZoom.set(this.map, fadeZoom);
      if (this.layerExists(LayerId.HIGHEST_PERFORMING_COMPETITOR_CROWN)) {
        this.map.setLayerZoomRange(
          LayerId.HIGHEST_PERFORMING_COMPETITOR_CROWN,
          minZoom,
          HIGHEST_COMPETITOR_MARK_MAX_ZOOM,
        );
        this.syncHighestPerformingCompetitorFade();
      }
    }
    const placed = highestCompetitorPlaced.get(this.map);
    const point: GeoJSON.Feature<GeoJSON.Point> = {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [longitude, latitude] },
      properties: {},
    };

    if (!this.sourceExists(SourceId.HIGHEST_PERFORMING_COMPETITOR)) {
      this.map.addSource(SourceId.HIGHEST_PERFORMING_COMPETITOR, { type: 'geojson', data: point });
      highestCompetitorPlaced.set(this.map, { longitude, latitude });
      return minZoom;
    }

    if (placed?.longitude === longitude && placed.latitude === latitude) return minZoom;

    const source = this.map.getSource(
      SourceId.HIGHEST_PERFORMING_COMPETITOR,
    ) as mapboxgl.GeoJSONSource;
    source.setData(point);
    highestCompetitorPlaced.set(this.map, { longitude, latitude });
    return minZoom;
  }

  /**
   * A station inside the tile rounding. One whose charges match this winner
   * beats a closer neighbor. With no match, the closest station is used.
   * Electra is skipped. The stored coordinate stays when the tiles have none.
   */
  private snappedTileStation(
    longitude: number,
    latitude: number,
    performance: number,
  ): mapboxgl.GeoJSONFeature | null {
    if (!this.sourceExists(SourceId.COMPETITORS_TILES)) return null;

    let features: mapboxgl.GeoJSONFeature[] = [];
    try {
      features = this.map.querySourceFeatures(SourceId.COMPETITORS_TILES, {
        sourceLayer: COMPETITORS_TILES_SOURCE_LAYER,
        filter: ['has', 'station_id'],
      });
    } catch {
      return null;
    }

    let matched: mapboxgl.GeoJSONFeature | null = null;
    let matchedMeters = TILE_STATION_SNAP_METERS;
    let nearest: mapboxgl.GeoJSONFeature | null = null;
    let nearestMeters = TILE_STATION_SNAP_METERS;
    for (const feature of features) {
      if (feature.properties?.station_id == null || feature.geometry?.type !== 'Point') continue;
      if (feature.properties.cpo === CPO_ELECTRA) continue;
      const [featureLon, featureLat] = feature.geometry.coordinates;
      const meters = distanceMeters(longitude, latitude, featureLon, featureLat);
      if (meters > TILE_STATION_SNAP_METERS) continue;

      if (meters <= nearestMeters) {
        nearestMeters = meters;
        nearest = feature;
      }
      const charges = feature.properties.charges_per_station_per_day;
      if (
        typeof charges === 'number' &&
        Math.abs(charges - performance) <= TILE_STATION_CHARGE_MATCH &&
        meters <= matchedMeters
      ) {
        matchedMeters = meters;
        matched = feature;
      }
    }
    return matched ?? nearest;
  }

  /**
   * Fades the crown with the station tier that owns this winner. Same rule as
   * `syncCompetitorsTilesFade`: one step across `fadeZoom`, and no rewrite
   * while the value already matches, so a zoom gesture does not restart the
   * transition.
   */
  syncHighestPerformingCompetitorFade() {
    const fadeZoom = highestCompetitorFadeZoom.get(this.map);
    const crownId = LayerId.HIGHEST_PERFORMING_COMPETITOR_CROWN;
    if (fadeZoom == null || !this.layerExists(crownId)) return;

    const opacity = this.map.getZoom() >= fadeZoom ? 1 : 0;
    if (this.map.getPaintProperty(crownId, 'icon-opacity') !== opacity) {
      this.map.setPaintProperty(crownId, 'icon-opacity', opacity);
    }
  }

  /** Drops the crown. Safe when it was never added. */
  clearHighestPerformingCompetitor() {
    unbindHighestCompetitorFade(this.map);
    unbindHighestCompetitorSnap(this.map);
    highestCompetitorFadeZoom.delete(this.map);
    highestCompetitorTarget.delete(this.map);
    highestCompetitorPlaced.delete(this.map);
    if (this.layerExists(LayerId.HIGHEST_PERFORMING_COMPETITOR_CROWN)) {
      this.map.removeLayer(LayerId.HIGHEST_PERFORMING_COMPETITOR_CROWN);
    }
    if (this.sourceExists(SourceId.HIGHEST_PERFORMING_COMPETITOR)) {
      this.map.removeSource(SourceId.HIGHEST_PERFORMING_COMPETITOR);
    }
  }

  /**
   * Filter routes based on multiple traffic ranges with nested logic.
   * Each filter shows data based on what other filters are selected.
   * Ranges match the `TRAFFIC_AADT_LINE_COLOR` interpolation.
   */
  filterTraffic(layerId: string, ranges: TrafficFilters[]) {
    if (!this.map.getLayer(layerId)) return;

    // Build nested filters based on selected ranges
    const filters: mapboxgl.FilterSpecification[] = [];

    // 0-1k: 0 to 1000
    if (ranges.includes(TRAFFIC_COLORS.RANGE_0_1000.id)) {
      filters.push([
        'all',
        ['>=', ['get', 'aadt'], TRAFFIC_THRESHOLDS.RANGE_0_1000],
        ['<=', ['get', 'aadt'], TRAFFIC_THRESHOLDS.RANGE_1000_8000],
      ]);
    }

    // 1k-8k: 1000 to 8000
    if (ranges.includes(TRAFFIC_COLORS.RANGE_1000_8000.id)) {
      filters.push([
        'all',
        ['>', ['get', 'aadt'], TRAFFIC_THRESHOLDS.RANGE_1000_8000],
        ['<=', ['get', 'aadt'], TRAFFIC_THRESHOLDS.RANGE_8000_15000],
      ]);
    }

    // 8k-15k: 8000 to 15000
    if (ranges.includes(TRAFFIC_COLORS.RANGE_8000_15000.id)) {
      filters.push([
        'all',
        ['>', ['get', 'aadt'], TRAFFIC_THRESHOLDS.RANGE_8000_15000],
        ['<=', ['get', 'aadt'], TRAFFIC_THRESHOLDS.RANGE_15000_44000],
      ]);
    }

    // 15k-44k: 15000 to 44000
    if (ranges.includes(TRAFFIC_COLORS.RANGE_15000_44000.id)) {
      filters.push([
        'all',
        ['>', ['get', 'aadt'], TRAFFIC_THRESHOLDS.RANGE_15000_44000],
        ['<=', ['get', 'aadt'], TRAFFIC_THRESHOLDS.RANGE_44000_PLUS],
      ]);
    }

    // 44k+: > 44000
    if (ranges.includes(TRAFFIC_COLORS.RANGE_44000_PLUS.id)) {
      filters.push(['>', ['get', 'aadt'], TRAFFIC_THRESHOLDS.RANGE_44000_PLUS]);
    }

    // Combine all filters with 'any' to show data from any selected range
    const finalFilter: mapboxgl.FilterSpecification = ['any', ...filters];

    this.map.setFilter(layerId, finalFilter);
  }

  /**
   * Filter stations based on charges_per_station_per_day ranges.
   * Each filter shows data based on what other filters are selected.
   * Only applies to competitor stations - Electra stations visibility controlled separately.
   * @param minHpcSpots - Minimum operative_hpc_spots required (0 = no filter)
   */
  filterStations(
    layerId: string,
    ranges: StationChargeFilters[],
    showElectra = true,
    minHpcSpots = 0,
  ) {
    if (!this.map.getLayer(layerId)) return;

    // Build nested filters based on selected ranges
    const filters: mapboxgl.FilterSpecification[] = [];

    const chargesField = ['coalesce', ['get', 'charges_per_station_per_day'], 0];

    for (const range of STATION_CHARGE_RANGE_BOUNDS) {
      if (!ranges.includes(range.label)) continue;

      const clauses: mapboxgl.ExpressionSpecification[] = [];
      if (range.min != null) clauses.push(['>=', chargesField, range.min]);
      if (range.max != null) clauses.push(['<', chargesField, range.max]);
      if (clauses.length === 1) {
        filters.push(clauses[0] as mapboxgl.FilterSpecification);
      } else if (clauses.length > 1) {
        filters.push(['all', ...clauses]);
      }
    }

    // Combine range filters with 'any' to show data from any selected range
    const rangeFilter: mapboxgl.FilterSpecification = ['any', ...filters];

    // HPC spots filter (only for competitor stations)
    const hpcSpotsField = ['coalesce', ['get', 'operative_hpc_spots'], 0];
    const hpcFilter: mapboxgl.FilterSpecification =
      minHpcSpots > 0 ? ['>=', hpcSpotsField, minHpcSpots] : ['literal', true];

    // Competitor stations filter: not electra AND matches range AND meets HPC spots requirement
    const competitorFilter: mapboxgl.FilterSpecification = [
      'all',
      ['!=', ['get', 'cpo'], CPO_ELECTRA],
      rangeFilter,
      hpcFilter,
    ];

    // Build final filter based on showElectra flag
    let finalFilter: mapboxgl.FilterSpecification;
    if (showElectra) {
      // Show Electra stations OR filtered competitor stations
      finalFilter = ['any', ['==', ['get', 'cpo'], CPO_ELECTRA], competitorFilter];
    } else {
      // Show only filtered competitor stations (hide Electra)
      finalFilter = competitorFilter;
    }

    // A tier's own predicate has to survive whatever the user filters down to.
    const tierFilter = competitorTierFilter(layerId);
    this.map.setFilter(layerId, tierFilter ? ['all', tierFilter, finalFilter] : finalFilter);
  }

  /**
   * Filter captured AADT segments based on segment IDs.
   * Shows only segments whose feature id matches one of the provided IDs.
   * Filters both the line layer and marker layer.
   * @param segmentIds - Array of segment IDs to show (e.g., ['0', '1'])
   */
  filterCapturedAadt(segmentIds: CapturedAadtSegmentId[]) {
    if (segmentIds.length === 0) {
      // Hide all segments via impossible filter
      if (this.layerExists(LayerId.CAPTURED_AADT_BORDER)) {
        this.map.setFilter(LayerId.CAPTURED_AADT_BORDER, ['==', ['id'], -1]);
      }
      if (this.layerExists(LayerId.CAPTURED_AADT)) {
        this.map.setFilter(LayerId.CAPTURED_AADT, ['==', ['id'], -1]);
      }
      if (this.layerExists(LayerId.CAPTURED_AADT_MARKER)) {
        this.map.setFilter(LayerId.CAPTURED_AADT_MARKER, ['==', ['get', 'segmentId'], '__none__']);
      }
      return;
    }

    // Filter line layers (border and main) by feature id
    const lineFilter: mapboxgl.FilterSpecification = [
      'in',
      ['to-string', ['id']],
      ['literal', segmentIds],
    ];

    if (this.layerExists(LayerId.CAPTURED_AADT_BORDER)) {
      this.map.setFilter(LayerId.CAPTURED_AADT_BORDER, lineFilter);
    }
    if (this.layerExists(LayerId.CAPTURED_AADT)) {
      this.map.setFilter(LayerId.CAPTURED_AADT, lineFilter);
    }

    // Filter marker layer by segmentId property
    if (this.layerExists(LayerId.CAPTURED_AADT_MARKER)) {
      const markerFilter: mapboxgl.FilterSpecification = [
        'in',
        ['get', 'segmentId'],
        ['literal', segmentIds],
      ];
      this.map.setFilter(LayerId.CAPTURED_AADT_MARKER, markerFilter);
    }
  }

  /**
   * Clear captured AADT filter to show all segments.
   */
  clearCapturedAadtFilter() {
    if (this.layerExists(LayerId.CAPTURED_AADT_BORDER)) {
      this.map.setFilter(LayerId.CAPTURED_AADT_BORDER, null);
    }
    if (this.layerExists(LayerId.CAPTURED_AADT)) {
      this.map.setFilter(LayerId.CAPTURED_AADT, null);
    }
    if (this.layerExists(LayerId.CAPTURED_AADT_MARKER)) {
      this.map.setFilter(LayerId.CAPTURED_AADT_MARKER, null);
    }
  }

  // Clear all filters
  clearRoutesFilter(layerId: string) {
    if (this.layerExists(layerId)) {
      this.map.setFilter(layerId, null);
    }
  }
}
